import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import track from "../../../../utils/track";
import generalService from "../../../../service/general-service";
import { startTeaser } from "../feihong-teaser-cache";
import {
  STORY_LINE,
  isPairLine,
  storyLineOf,
} from "../../../../constant/feihong-story-line";
import PaymentService from "../../../../service/payment-service";
import InvoiceSheet from "../../../dialog/invoice-sheet/invoice-sheet";
import {
  openEcpayWindow,
  submitToEcpay,
} from "../../../../utils/ecpay-redirect";
import FeihongStoryIntro from "./feihong-story-intro";
import PurchaseSheet from "../../../dialog/purchase-sheet/purchase-sheet";
import "./feihong-story-style.scss";

//—— 緋紅・八字合婚：解命後的故事頁 ——
//
//  流程上接在 match-feihong-page 的最後一幕（reading）之後：
//  解命影片播完 → 導到這裡。
//
//  和前面那一頁的差別是「版型」而不只是內容：
//  劇情頁是滿版固定高度、一次只給一幕、不能捲；這一頁是一路捲到底的長頁。
//  兩種版型塞進同一個元件會變成一堆 if，所以拆成兩支。
//
//  內容全在下面 STORY 這張表裡，元件只負責排版 ——
//  改文案、加段落、調順序都只動那張表，作法同劇情頁的 SCENES。

//回上一頁（劇情頁）的去處。直接貼網址進來時沒有上一頁，要有個退路。
const BACK_TO = "/product/match-feihong";

//—— 開發用的替身問診資料 ——
//  location state 不進網址，也撐不過重新整理 —— 直接開這一頁或按 F5，
//  intake 就是空的，頁面會把人導回劇情頁。
//
//  但這一頁的版型要反覆調（圖、對話框位置、字級都得看著畫面改），
//  每改一次就從問診走十題不切實際。開發時用一組固定的假資料頂著，
//  網址列直接打 /product/match-feihong/story 就能看到完整畫面。
//
//  只在 npm start 生效：NODE_ENV 在建置時就會被換成字面值 "production"，
//  所以正式 build 裡這個常數是 null，整段假資料也會被搖掉，不會進 bundle。
//
//  生日刻意挑一個「有時辰、有出生地」的：這樣真太陽時校正那條路徑
//  平常就會被走到，不會等到上線才第一次跑。
//底部按鈕的觸發點：使用者滑到這個元素，按鈕就出現，之後不再消失。
//  放在「是為了那件事來的吧？」那一行 —— 在那之前他還在鋪陳，
//  按鈕太早出現會變成在打斷故事。
const CTA_TRIGGER_ID = "feihong-story-cta-trigger";

//付款開在新分頁之後，這一頁靠輪詢知道結果（見 watchOrder）。
//  3 秒一次：使用者在綠界刷完卡大概會停留幾秒看成功畫面，
//  這個間隔讓他切回來時多半已經變了，又不至於打太兇。
//  15 分鐘封頂：綠界那張單本來就會過期，再等下去沒有意義。
const PAY_POLL_MS = 3000;
const PAY_POLL_LIMIT_MS = 15 * 60 * 1000;

//—— 購買方案 ——
//
//  欄位名就是劇情頁 SCENES 的 field（見 match-feihong-page）——
//  跑掉了就會出現「預覽看起來正常、真的走一遍卻空一塊」。
//  生辰跟示意圖一致（施全付 1995/12/13、對方 1995/02/08），
//  比對版型時畫面上的四柱跟圖上的對得起來。
const SAMPLE_INTAKE = {
  intent: "pair",
  status: "dating",
  name: "施全付",
  gender: "male",
  birthDate: "1995/12/13",
  birthTime: "16:20",
  birthPlace: { label: "臺灣 臺北市", lat: 25.033, lng: 121.5654 },
  partnerGender: "female",
  partnerBirthDate: "1995/02/08",
  partnerBirthTime: "09:40",
  partnerBirthPlace: { label: "臺灣 臺中市", lat: 24.1477, lng: 120.6736 },
  concern: "",
  email: "sample@example.com",
};


//預覽時指定走哪一條線：?preview&line=single／ambiguous／together。
//  三條線的版型要分別看，而「走哪一條」是問診答案推出來的
//  （storyLineOf）—— 沒有這個旋鑼就要每次改 SAMPLE_INTAKE 再重新整理。
//
//  刻意是「覆寫問診答案」而不是「直接指定 BLOCKS」：
//  這樣走的還是跟正式一模一樣的 storyLineOf，
//  分線的邏輯寫錯時預覽也會一起錯，不會被繞過去。
const PREVIEW_LINE = {
  single: { intent: "solo", status: "single" },
  ambiguous: { intent: "pair", status: "ambiguous" },
  together: { intent: "pair", status: "dating" },
};

const sampleIntakeFor = (search) => {
  const over = PREVIEW_LINE[new URLSearchParams(search).get("line")];
  return over ? { ...SAMPLE_INTAKE, ...over } : SAMPLE_INTAKE;
};

//這一頁在什麼情況下可以用範例資料顯示：
//
//  開發時（npm start）—— 版型要反覆調，每次從問診走十題不實際。
//  網址帶 ?preview  —— 給人看用的。location.state 不進網址，
//                      所以把連結傳給別人時對方一定沒有問診資料。
//
//  預覽模式刻意做成「網址帶參數」而不是另開一條路由：
//  它走的是同一支元件、同一組 block，看到的就是真的那一頁，
//  不會出現「預覽版跟正式版長得不一樣」這種事。
//  刻意不取 use 開頭的名字：那會被 ESLint 的 react-hooks 規則
//  當成 Hook，而它是在三元運算式裡呼叫的，會被判定成「條件式呼叫 Hook」。
const wantsSampleIntake = (search) =>
  process.env.NODE_ENV === "development" ||
  new URLSearchParams(search).has("preview");

//—— 問診資料 ——
//
//  劇情頁走完會把答案放在 location state 的 intake 裡帶過來（見 match-feihong-page
//  最後一幕的導頁）。欄位名就是 SCENES 裡的 field，值也沒有轉換過：
//
//    relation      看誰的八字   self / family / partner / friend / other
//    birthDate     出生年月日   "1990/01/01"（國曆）
//    birthTime     出生時間     "12:30"，或 "unknown"
//    birthPlace    出生地       BirthplaceField 給的物件（含經緯度）
//    gender        性別         "male" / "female"（排盤要的 "1"/"0" 之後再轉）
//    name          稱呼
//    orientation   取向         hetero / lgbt
//    relationship  感情狀態     single / crush / dating / breakup / married
//    career        工作狀態     student / seeking / employed / business / freelance / none
//    concern       想問的煩惱   可留白
//    email         通知信箱
//
//  重新整理就會沒有 —— location state 不進網址也不落地。
//  所以沒接到資料時不硬撐著畫面，直接把人送回劇情頁從頭走一次。

//—— 故事內容 ——
//
//  整頁是一條長捲軸，由一個個 block 串起來，中間沒有間隙也沒有頁的概念。
//  要加內容就往這張表後面加一筆，版型不用動。
//
//  目前有四種 block：
//    image  滿版圖片，高度依原圖比例，接縫處對齊沒有空隙；
//           可疊任意顆對話框（bubbles）或字幕（captions）
//    text   黑底文字，一行一句（lines 給幾行就幾行）；
//           帶 size: "lg" 的話整段字一起放大，用在要壓過周圍的段落；
//           帶 head / tail 的話上緣／下緣多留一截，用來跟前後拉開距離
//    panel  花紋背景上嵌一塊黑板子，寫標題 + 大段內文（可接動態資料）
//    chart  四柱命盤表（十神／天干／地支／藏干），資料全部來自後端
//    chapter 章節目錄卡：膠囊標籤 + 標題 + 條列，字直接壓在花紋上
//    reviews 用戶回饋列表：遮罩信箱 + 星等 + 評語
//
//  之後要新的樣式（例如兩欄、或圖上疊字），就多一種 type，
//  在底下的 renderBlock 加一個分支 —— 不要把版型判斷寫進資料表裡。
//  ⚠️ 暫時頂著用的白夜素材。
//  緋紅的故事頁圖還沒畫，整包從白夜抄過來時圖也跟著抄 ——
//  所以 ${IMG} 指著 baiye 的資料夾，檔名也還是 baiye-story-*。
//
//  圖是一張一張換上去的（同鏡月當初的作法）：換好一張就把那一塊的
//  檔名改成 feihong-story-*，並讓 imgUrl 依檔名挑資料夾（見下面）。
//  全部換完就把 BORROW 與 imgUrl 收掉，只留一個常數。
const IMG = `${process.env.PUBLIC_URL}/images/feihong/`;
const BORROW = `${process.env.PUBLIC_URL}/images/baiye/`;

//  依檔名決定去哪個資料夾找。
//  不用「先試緋紅、載不到退回白夜」—— 那樣每張還沒換的圖都會送出一個
//  註定 404 的請求（dev 環境會被 proxy 轉給後端，見 server.js 的放行規則）。
const imgUrl = (file) =>
  (String(file).startsWith("feihong-") ? IMG : BORROW) + file;

//三條線共用同一批用戶回饋。
//  信箱是遮罩過的，評語裡的換行照使用者原本打的斷。
const REVIEWS = [
      {
        email: "sh******@gmail.com",
        stars: 5,
        text: "原本以為只是普通的八字測算，結果緋紅講我「為了防禦心錯過兩次緣分」完全講中！連我對擇偶的龜毛點都剖析得超透徹。\n看完報告真的有被點醒的感覺😍 期待大運提到的正緣時機",
      },
      {
        email: "vi*****@yahoo.com.tw",
        stars: 5,
        text: "文字古風感滿滿超有沉浸感！\n緋紅對我感情性格的分析針針見血 直接把我深夜偷哭的嬌情心態講出來\n重點是內容超級豐富 完全不是那種套公式的罐頭文字 強烈推薦給還在迷茫的人！",
      },
      {
        email: "mi*****@gmail.com",
        stars: 5,
        text: "跟男友最近常為了小事大吵 本來有點想放棄這段關係😣\n但發現測算報告把我倆夫妻宮相沖、還有交往後相處的爆點全都講對了！\n現在重新面對了 緋紅給的轉化建議非常中肯實用",
      },
      {
        email: "ke****@gmail.com",
        stars: 5,
        text: "跟對象卡在曖昧期快半年，進退兩難快被逼瘋。緋紅直接把對方「忽冷忽熱的背後心態」跟合盤衝突點拆解得一清二楚！\n看完我鼓起勇氣照著建議調整態度，隔天對方竟然主動約我了，太神！",
      },
      {
        email: "an*****@hotmail.com",
        stars: 5,
        text: "測完發現花這個錢超值！\n把我們兩個八字互相吸引又互相猜忌的原因講得超精準\n連對方骨子裡的防禦機制都說中了 直接省下我猜心意的時間！！！",
      },
      {
        email: "cy*****@yahoo.com.tw",
        stars: 5,
        text: "文筆非常絕美且有份量！\n原本以為結婚後測這個沒意思，結果報告裡關於兩人家運流轉和未來幾年需要注意的感情考驗非常有參考價值👍\n語氣既像妖魅調侃又像長輩寵溺，閱讀體驗極佳！",
      },
      {
        email: "pe*****@gmail.com",
        stars: 5,
        text: "這絕對是我買過最精細的感情八字報告\n讀完我和男友開誠布公聊了一整晚\n感情反而變更好了！",
      },
      {
        email: "e1*****@gmail.com",
        stars: 5,
        text: "跟老婆結婚三年，最近進入平淡期總覺得少了點什麼。抱著好奇心測了合盤，沒想到緋紅連我們當年怎麼被對方吸引、以及婚後各自隱藏的壓力都寫得絲毫不差。\n看完心裡暖暖的，找回了當初相愛的感覺😊",
      },
];

//—— 單身線的故事頁 ——
//
//  緋紅有三條故事線（見 constant/feihong-story-line）。這一份是**單身線**：
//  問診走 solo 路徑的人，手上只有他自己一張盤，所以整頁的主詞是「你」不是
//  「你們」—— 沒有合盤、沒有對方的四柱，講的是「會來什麼人、什麼時候來」。
//
//  另外兩條線各有自己一份（BLOCKS_AMBIGUOUS／BLOCKS_TOGETHER）——
//  它們有兩張盤、主詞是「你們」，卷名也完全不同，不是換字就能共用的。
//
//  圖片編號就是頁面順序：feihong-story-1 是開場第一張，15 是收口那張。
//  底圖的分工（Kevin 2026-10-20 指定）：
//    bg-1  八字命盤那一塊
//    bg-2  六卷的章節卡
//    bg-3  四塊命書面板
//    bg-4  橫向的花飾分隔（title 區塊上方那條）
const BLOCKS_SINGLE = [
  //1. 開場。她托腮看著你 —— 不挑釁也不招呼，就是在等你問。
  { type: "image", src: imgUrl("feihong-story-1.png") },

  //2. 第一句話就把商品說清楚：這一頁在講「你的情緣劇本」。
  //  上下留得比別塊多（Kevin 2026-10-20）—— 這是整頁的第一句話，
  //  前後都是滿幅的圖，用預設的 50／30 會變成「圖縫裡夾了兩行字」。
  //  數字不寫死在這裡：--head-lg／--tail-lg 是版型已經有的一級（88／90px），
  //  別處要同樣的氣口就跟著用同一組，不要再調一個新的。
  {
    type: "text",
    head: "lg",
    tail: "lg",
    lines: [
      "來看看這漫天飄落的桃花",
      { text: "為你纏繞了怎樣的情緣劇本", style: "gradient-soft" },
    ],
  },

  //3.
  {
    type: "image",
    src: imgUrl("feihong-story-2.png"),
    //  框等比縮小（Kevin 2026-10-20）。只改 width 就好 ——
    //  高度是 aspect-ratio 撐出來的、字級是框寬 × ratio，兩個都會跟著縮。
    bubbles: [{ text: "嗯…", top: "16%", left: "8%", width: "22%" }],
  },

  //4. 單身線的痛點就是這一題。用問句不用斷言 ——
  //   「你很寂寞」會被否認，「多久了？」會被回答。
  //  上下氣口跟開場那塊同一級（Kevin 2026-10-20）。
  //  標題行改用 jade-deep：粗體、紅壓深，字距與光暈照舊。
  //
  //  ⭐ gap 從標題行搬到下一行。
  //  has-gap 加的是 margin-top，寫在標題行上是把它自己往下推（跟塊的上緣留白重複），
  //  要的是「標題跟內文拉開」，所以要寫在「表面上…」那一行。
  {
    type: "text",
    //  三句各自寫 sm（25px），標題行是 lg（30px）。
    //  寫在行上而不是用 block 的 size：標題行跟內文各有各的級，
    //  用 block 的 size 會把兩者綁在一起，調一個動到另一個。
    //
    //  上下留白收到 50／60：字變大之後整塊本來就變高了，
    //  再留 68／90 會變成一大片空白夾四行字。
    tail: true,
    lines: [
      { text: "獨自一個人多久了？", style: "jade-deep lg" },
      { text: "表面上說著習慣孤單", style: "sm", gap: true },
      { text: "其實每次看到街上的情侶", style: "sm" },
      { text: "心裡還是會酸一下吧？", style: "sm" },
    ],
  },

  //5. 四柱。from: "pillars" —— 內容要等排盤回來，不寫死在這裡。
  //
  //  位置與尺寸都是量示意圖來的（Kevin 2026-10-20 的圖）：
  //    框 x 548–900 / y 897–1379，圖本身 899×1196
  //    → left 49.4%、top 60.3%、width 39.2%、寬高比 0.730
  //    字級 58.5px ÷ 框寬 352px = ratio 0.166
  //  框的大小跟字級本來就是對的（舊值 0.17 / 0.72），錯的只有位置：
  //  舊的 left: 8% 把框放在左邊，示意圖是在她背後的右下角。
  //
  //  glow: true 是筆畫外圈那層粉光 —— 量示意圖筆畫邊緣是 rgb(186,144,159)，
  //  R 明顯大於 G，是真的有一層粉，不是抗鋸齒。
  {
    type: "image",
    src: imgUrl("feihong-story-3.png"),
    bubbles: [
      {
        from: "pillars",
        top: "60%",
        left: "49%",
        width: "39%",
        ratio: 0.166,
        aspect: "0.73",
        glow: true,
      },
    ],
  },

  //6. 叫他的名字。問診填的暱稱，整頁唯一一次直接喊人。
  { type: "text", tail: true, lines: [{ from: "name", style: "name" }] },

  //7.
  { type: "image", src: imgUrl("feihong-story-4.png") },

  //8. 先替他翻案（不是你不夠好），再把問題移到「你還沒看懂」——
  //   那是賣報告的支點：問題有解，而解法在這份報告裡。
  {
    type: "text",
    //  size: "xs" 是字級， wide 是左右內距 —— 兩個是分開的旋鑼，
    //  這一塊兩個都要（字要統一小、且最長那句要擺得下）。
    size: "xs",
    wide: true,
    //  上下等距且比預設寬（Kevin 2026-10-20）。
    even: true,
    //  四行全部是漸層字（Kevin 2026-10-20）。
    //
    //  ⭐ 連帶把紅色色塊（is-mark）拿掉了 —— 不是漏改，是不能留：
    //  is-gradient-soft 的漸層收在 #6e1228（深緋紅），色塊是 #8c0026，
    //  字的下半段會直接沒入底色裡。兩者只能擇一。
    //  （開場那塊走過同一條路：先試「漸層字 + 色塊」，最後定案是只留漸層字。）
    lines: [
      //  前三句自己寫 md（28px），比 block 的 24px 大一級。
      //  這三句最長的是 13 字，在 12px 內距下上限是 31.6px，還有餘裕；
      //  最後那行 17 字才是卡住整塊的那一句，所以它留在 24px。
      { text: "這不是你不夠好", style: "gradient-soft md" },
      { text: "也不是你不值得被愛", style: "gradient-soft md" },
      { text: "是你還沒看懂八字牽下的紅線", style: "gradient-soft md" },
      //  tight：線的上下留白從 34px 收到 18px。
      //  下一行的 gap 也拿掉了 —— 線本身就是分隔，再加一個氣口是疊兩層。
      { divider: "tight" },
      {
        text: "是為了「那個人」何時出現而來的吧？",
        style: "gradient-soft",
        //  滑到這一句，底部的購買按鈕就出現（跟白夜、鏡月同一個位置）。
        id: CTA_TRIGGER_ID,
      },
    ],
  },

  //9.
  //  ⭐ 這題框是「掛出圖片下緣」的，所以要給 spill。
  //  量示意圖（Kevin 2026-10-20 的 132.png）：
  //    圖片 x 88–987（寬 899）、頂 y 107，依原檔比例 721×1095 推得底 y 1472
  //    橢圓 x 350–725（寬 375）、y 1273–1720（高 447）
  //    → left 29%、top 85%、width 42%、寬高比 0.84
  //    → 橢圓底 1720 − 圖底 1472 = 248px = 圖寬的 28%
  //    字級 58px ÷ 框寬 375px = ratio 0.155（預設 0.29 會破框，舊值量過寬超 28px）
  //
  //  spill 給的是「佔圖寬的百分比」不是圖高 —— 百分比的 margin
  //  一律以包含區塊的**寬度**為基準（與 padding 同一個坑）。
  {
    type: "image",
    src: imgUrl("feihong-story-5.png"),
    //  ⭐ spill 要跟著 aspect 重算，改了一個忘了另一個框就會壓到下一塊：
    //    框高 = 框寬 ÷ aspect = 176 ÷ 0.76 = 232px
    //    框底 = 圖高×85% + 232 = 541 + 232 = 773
    //    spill = (773 − 圖高 637) ÷ 圖寬 420 = 32%
    spill: "32%",
    bubbles: [
      {
        text: "牽著這根\n緋紅絲線",
        top: "85%",
        left: "29%",
        width: "42%",
        ratio: 0.155,
        //  0.76：比量示意圖的 0.839 高、但沒到 7:10 那麼高
        //  （Kevin 先要「再高一點」、再要「小一點點」，來回調出來的）。
        //  框寬不變，只動高度：210 → 232px。
        //  （字級是從**寬**推的，所以拉高不會把字跨大。）
        aspect: "0.76",
      },
    ],
  },

  //10.
  {
    type: "text",
    size: "sm",
    lines: [
      //  兩行同字級（md）—— 量示意圖就是這樣：59px vs 61px，幾乎一樣。
      //  兩句的對比不是靠大小，是靠**字重與光**：
      //  上一句 weight 500 無光、下一句 weight 700 帶紅光。
      { text: "看看這滿園桃花霧氣", style: "md" },
      //  md（28px）：比上一句大一級。
      //  11 字 × 28 = 308px，文字區內寬 372px，還有餘裕。
      { text: "究竟藏著什麼宿命的秘密", style: "strong md", gap: "sm" },
    ],
  },

  //11. 命書面板①。內文由 AI 依他的八字生成（bodyFrom）。
  {
    type: "panel",
    bg: imgUrl("feihong-story-bg-3.jpg"),
    //  上緣的間距主要是面板自己的 padding（100px）撐的，
    //  外面這一截只要 22px（--head-xs）—— 數字是量示意圖來的，見 scss。
    head: "xs",
    title: "你現在的狀態是",
    bodyFrom: "situation",
  },

  //12. 把上面那段 AI 寫的東西歸因到命盤 —— 這一句是整頁的轉折：
  //    從「她很準」變成「因為盤上寫著」，報告才有存在的理由。
  //  三行全部放大，而且有大小差（Kevin 2026-10-20）：
  //    看到這裡            lg 30px
  //    終於明白…累了       lg 30px
  //    全是你的命盤寫好的   lg 30px
  //
  //  ⭐ 三行同字級。本來「看到這裡」是 md（28px）、兩者差一級，
  //  但 Kevin 要它再大一點 —— 而 md 跟 lg 只差 2px，再大一級就是 lg。
  //  語氣的差別現在靠字重：前兩行 500、最後一行 700。
  //
  //  ⭐ wide: true 是必要的 —— 不收內距的話 lg 在 390px 視窗會折行：
  //  「終於明白你為何愛得這麼累了」13 字，
  //  內距 24px 時上限 29.7px、收到 12px 就是 31.8px。
  {
    type: "text",
    head: "lg",
    tail: "lg",
    wide: true,
    lines: [
      { text: "看到這裡", style: "lg" },
      { text: "終於明白你為何愛得這麼累了", style: "lg" },
      { parts: ["全是你的", { text: "命盤" }, "寫好的"], style: "strong flat lg", gap: "lg" },
    ],
  },

  //13. 八字命盤表。格式與白夜那邊完全一致（四柱＋十神＋藏干），
  //    內容全部來自後端（from: "chart"），前端不做任何命理換算。
  { type: "chart", bg: imgUrl("feihong-story-bg-1.png"), from: "chart" },

  //14. 上 54px、下 66px —— 上緣比下緣短一截。
  //  （本來是 even: "lg" 的 66／66，Kevin 2026-10-21 要上面再小一點點。）
  //  兩行都放大到 lg。
  //  最長那句 12 字，內距 24px 時上限 32.2px（390px 視窗）——
  //  30px 還有餘裕，所以這一塊不需要 wide。
  {
    type: "text",
    head: "sm",
    tail: "md",
    lines: [
      { text: "那根纏在你指尖的命定紅線", style: "lg" },
      { text: "到底綁在誰的身上", style: "lg" },
    ],
  },

  //15.
  //  兩題框都掛出圖的下緣，右那題掛得很多。
  //  量示意圖（Kevin 2026-10-21 的 138.png），圖片欄 899px、原檔 1500×1028：
  //
  //            框尺寸      left   top    width  寬高比  ratio  spill
  //    指尖紅絲  266×364  10.9%  54.4%  29.6%  0.731  0.182   9.2%
  //    你敢不敢  375×513  45.2%  92.8%  41.7%  0.731  0.161  52.2%
  //
  //  spill 取兩題裡掛最低的那一題（右邊的 52%）——
  //  它是「這一塊要讓出多少空間」，不是每題各算一個。
  //
  //  左那題的文字要手動斷行：「指尖紅絲已動…」7 字在 124px 的框裡
  //  要 158px（white-space: pre 不會自己折）—— 示意圖斷在「已」後面。
  //
  //  右那題的字有粉光（glow: true），左那題沒有 ——
  //  量出來左框四周只有 302 個偏粉的點（抗鋸齒），右框有 26000 個。
  {
    type: "image",
    src: imgUrl("feihong-story-6.png"),
    spill: "52%",
    bubbles: [
      {
        text: "指尖紅絲已\n動…",
        top: "54%",
        left: "11%",
        width: "30%",
        ratio: 0.182,
        aspect: "0.73",
      },
      {
        text: "你敢不敢\n窺見自己的\n感情定數？",
        top: "93%",
        left: "45%",
        width: "42%",
        ratio: 0.161,
        aspect: "0.73",
        glow: true,
      },
    ],
  },

  //16.
  //  三行都放大到 lg，上下留白加大（Kevin 2026-10-21）。
  //  最長那句「這根紅線正為你劃破長夜」11 字，
  //  內距 24px 時的上限是 35.1px（390px 視窗）—— 30px 還很寬鬆，不需要 wide。
  {
    type: "text",
    head: true,
    tail: "md",
    lines: [
      { text: "看清了", style: "lg" },
      { text: "這根紅線正為你劃破長夜", style: "lg" },
      { text: "牽往未知的遠方", style: "fade lg" },
    ],
  },

  //17.
  { type: "image", src: imgUrl("feihong-story-7.png") },

  //18. 先示弱再逼問。「可不負責替你擦眼淚」是她的輕挑，
  //    「你，真有膽量聽？」才是鉤子 —— 問完就給答案，不留他思考的空檔。
  //  字級與色系都是量示意圖來的（Kevin 2026-10-21 的 140.png），
  //  換算成 420px 欄寬下的 CSS px：
  //
  //    不過…本妖只剝開真相   27.6px  白、weight 500        → md
  //    可不負責替你擦眼淚     28.1px  白、weight 500        → md
  //    你，真有膽量聽？       37.7px  #c72a4d 粗體、光往下   → jade-strong（42px）
  //    緋紅這就一字一句       32.9px  白、粗體、**無光**      → strong flat xl（34px）
  //    慢慢拆給你聽           23.4px  #acacac 漸層淡出      → fade xl
  //                                   （示意圖是 23.4px，但 Kevin 三輪要求放大：
  //                                     sm → md → lg → xl。實際排上去比示意圖大一截。）
  //
  //  ⭐ block 拿掉了 size: "sm" —— jade-strong 在 --sm 塊裡會被壓成 35px，
  //  而示意圖要的是 42px 那一級。其他行改成各自寫字級。
  //
  //  ⭐「緋紅這就一字一句」是 **flat**（關掉光暈）—— 量示意圖那一列，
  //  筆畫四周離 2px 已經是 (1,1,1)、4px 之後全是 0，沒有任何光。
  {
    type: "text",
    lines: [
      { text: "不過…本妖只剝開真相", style: "md" },
      { text: "可不負責替你擦眼淚", style: "md" },
      { text: "你，真有膽量聽？", style: "jade-strong", gap: true },
      { divider: true },
      { text: "緋紅這就一字一句", style: "strong flat xxl", gap: true },
      { text: "慢慢拆給你聽", style: "fade xxl bold" },
    ],
  },

  //19. 第一個大字。三個大字（宿命／時機／正緣）把整頁分成三段，
  //    每一段對應報告裡的一組卷。
  {
    type: "text",
    tail: "lg",
    lines: [{ ornament: imgUrl("feihong-story-bg-4.jpg") }, { title: "宿\n命" }],
  },

  //20.
  //  量示意圖（Kevin 2026-10-21 的 142.png），圖欄 899px、原檔 1500×2668：
  //
  //            框尺寸      left   top    width  寬高比  ratio  spill
  //    你現在    266×364   9.6%  50.0%  29.6%  0.731  0.192   0%
  //    人生關鍵的 439×599  40.3%  69.2%  48.8%  0.733  0.186  11.8%
  //
  //  ratio 是用**文字寬**算的，不是墨跡高：示意圖的文字寬 408px、
  //  一行五個字 → 字級 82px ÷ 框寬 439px = 0.186。
  //  用墨跡高÷0.88 算出來是 0.21，套上去字會超框 17px ——
  //  那是因為標點符號跟筆畫少的字墨跡比較矮，把平均拉偏了。
  //
  //  ⭐ 右那題比舊值大很多（40% → 49%），而且字有粉光：
  //  量出來右框筆畫四周有 19239 個偏粉的點，左框只有 22 個（抗鋸齒）。
  //  筆畫本身兩題都是黑的 —— 讀起來像深紅是那層粉光造成的。
  {
    type: "image",
    src: imgUrl("feihong-story-8.png"),
    spill: "23%",
    bubbles: [
      {
        text: "你現在\n正處於",
        //  ratio 比示意圖的 0.192 大一點（Kevin 2026-10-21）。
        //  框寬 126px、一行三個字，0.22 算出來字級 27.7px、文字寬 83px，
        //  距離框邊還很寬 —— 三個字的框本來就有餘裕。
        top: "50%",
        left: "10%",
        width: "30%",
        ratio: 0.22,
        aspect: "0.73",
      },
      {
        text: "人生關鍵的\n情緣轉折期",
        //  比示意圖再往下、字再小一級（Kevin 2026-10-21）。
        //  top 69 → 75%、ratio 0.186 → 0.16 → 0.14（兩輪要求縮小）。
        //  spill 跟著重算：
        //  框高 = 206 ÷ 0.73 = 282，框底 = 747×75% + 282 = 842，
        //  spill = (842 − 圖高 747) ÷ 圖寬 420 = 23%。
        top: "75%",
        left: "40%",
        width: "49%",
        ratio: 0.14,
        aspect: "0.73",
        glow: true,
      },
    ],
  },

  //21. 命書面板②
  //  上緣多留一截（Kevin 2026-10-21）。這一塊正好是 --head（200px）
  //  當初設計的場合：前一塊是帶溢出對話框的圖，那題框的下緣剛好落在這裡。
  //
  //  ⭐ 為什麼不是 --head-sm（90px）：**上下外距會合併**。
  //  前一塊的 spill 是圖寬的 23% = 97px 的 margin-bottom，
  //  跟這一塊的 margin-top 相鄰時取的是**最大值不是和**：
  //    90px  → max(97, 90) = 97，跟沒加一樣（量到對話框底到面板頂是 1px）
  //    200px → max(97, 200) = 200，對話框下方才真的空出 103px
  //  下次調這種「前一塊有 spill」的塊要記得這件事。
  {
    type: "panel",
    bg: imgUrl("feihong-story-bg-3.jpg"),
    head: true,
    title: "你的感情宿命\n是這樣流轉的",
    bodyFrom: "destiny",
  },

  //22.
  //  三行都放大到 lg（Kevin 2026-10-21）。
  //  最長那句「為你寂寞地開了百年」9 字，30px 下是 258px，
  //  文字區內寬 372px —— 很寬鬆，不需要 wide。
  {
    type: "text",
    head: true,
    tail: "md",
    lines: [
      { text: "這樹桃花", style: "lg" },
      { text: "為你寂寞地開了百年", style: "lg" },
      { text: "只等今朝", style: "fade lg" },
    ],
  },

  //23.
  {
    type: "text",
    tail: "lg",
    lines: [{ ornament: imgUrl("feihong-story-bg-4.jpg") }, { title: "時\n機" }],
  },

  //24.
  //  量示意圖（Kevin 2026-10-21 的 145.png），圖欄 899px、原檔 1500×2668：
  //
  //              框尺寸      left   top    width  寬高比  ratio  spill
  //    那麼…      266×364   9.6%  44.8%  29.6%  0.731  0.294   0%
  //    你的桃花運  439×599  40.3%  64.9%  48.8%  0.733  0.186  4.2%
  //
  //  兩題都沒有粉光（量到 420 / 612 個偏粉的點，是抗鋸齒的量級；
  //  block 20 那題有光的是 19239 個）。
  {
    type: "image",
    src: imgUrl("feihong-story-9.png"),
    spill: "17%",
    bubbles: [
      {
        text: "那麼…",
        //  比示意圖的 0.294 小兩級（Kevin 2026-10-21，兩輪）。
        top: "45%",
        left: "10%",
        width: "30%",
        ratio: 0.22,
        aspect: "0.73",
      },
      {
        text: "你的桃花運\n究竟會\n何時盛開？",
        //  比示意圖再往下、字再小兩級（Kevin 2026-10-21）。
        //  top 65 → 72%、ratio 0.186 → 0.14。spill 跟著重算：
        //  框高 282，框底 = 747×72% + 282 = 820，(820−747)÷420 = 17%。
        top: "72%",
        left: "40%",
        width: "49%",
        ratio: 0.14,
        aspect: "0.73",
      },
    ],
  },

  //25.
  //  字級與上下留白都是量示意圖來的（Kevin 2026-10-21 的 146.png），
  //  換算成 420px 欄寬下的 CSS px：
  //    字級            30.5～32.9px（文字寬 335px ÷ 11 字 = 30.5）
  //    區塊上緣 → 墨跡頂   89px
  //    墨跡底 → 區塊下緣   130px
  //
  //  字級：拿掉 md（30px），讓 jade-strong 在 --sm 塊裡走自己的 35px → 33.4px。
  //  留白：head-lg 88px → 墨跡頂 98px；tail-xl 130px → 墨跡底 119px。
  //  （墨跡跟內距差的是半行距 0.25em + 字面上緣的 0.04em）
  {
    type: "text",
    size: "sm",
    head: "lg",
    tail: "xl",
    //  wide 是為了 320px 的舊機：11 字 × 25.8px = 284px，
    //  而 320−40（小螢幕內距）= 280px —— 差 4px 就折了。收到 12px 就有 296px。
    wide: true,
    lines: [{ text: "看看紅線是怎麼說的吧？", style: "jade-strong" }],
  },

  //26.
  { type: "image", src: imgUrl("feihong-story-10.png") },

  //27.
  //  上下留白拉開（Kevin 2026-10-21），上緣再多一級：68 → 88px。
  //  前一塊是圖，圖自己的下緣有暗場，所以上面要留得比下面多才看得出來。
  {
    type: "text",
    head: "lg",
    tail: "md",
    lines: [
      { text: "你的正緣", style: "md" },
      { text: "降臨時刻已到", style: "md" },
    ],
  },

  //28. 命書面板③
  {
    type: "panel",
    bg: imgUrl("feihong-story-bg-3.jpg"),
    title: "你的桃花\n是這樣流轉的",
    bodyFrom: "peach",
  },

  //29. 「破除單身魔咒」是單身線專屬的說法 ——
  //    另外兩條線有對象，這句話對他們沒有意義。
  //  字級、色系、字重都是量示意圖來的（Kevin 2026-10-21 的 147.png），
  //  換算成 420px 欄寬下的 CSS px：
  //
  //    緋紅色的花瓣         31.1px  白、粗體、**無光**   → strong flat xl
  //    正在夜空中綻放        30.9px  #b4b4b4 漸層、粗體  → fade bold xl（32px）
  //                               （示意圖跟第一行差不多大，但 Kevin 要它小一級）
  //    此時正是你破除單身魔咒之際 26.8px  白、粗體、**有紅光**  → strong md
  //
  //  ⭐ 最後那行本來是 is-mark（紅色色塊），示意圖沒有色塊 ——
  //  是白字配紅光：筆畫四周離 3px 是 (71,6,24)、12px 還有 (32,1,7)。
  //  前兩行則完全沒光（四周量到全是 0），所以第一行要 flat。
  {
    type: "text",
    head: "lg",
    tail: "md",
    lines: [
      { text: "緋紅色的花瓣", style: "strong flat xl" },
      { text: "正在夜空中綻放", style: "fade bold xl" },
      { text: "此時正是你破除單身魔咒之際", style: "strong md", gap: "lg" },
    ],
  },

  //30.
  {
    type: "text",
    tail: "lg",
    lines: [{ ornament: imgUrl("feihong-story-bg-4.jpg") }, { title: "正\n緣" }],
  },

  //31.
  {
    type: "image",
    src: imgUrl("feihong-story-11.png"),
    //  量示意圖（Kevin 2026-10-21 的 148.png），圖欄 899px、原檔 1500×2688：
    //
    //              框尺寸      left   top     width  寬高比  ratio  粉光
    //    情線如    318×422   6.7%  −11.6%  35.4%  0.754  0.230  無（0 點）
    //    有些相遇  449×633  42.3%   63.3%  49.9%  0.709  0.189  有（36247 點）
    //
    //  ⭐ 上面那題的 top 是**負的** —— 它掛在圖的**上緣之外**。
    //  __frame 沒有 overflow: hidden，所以往上溢出畫得出來；
    //  上一塊是文字 block（也是純黑底），而且 DOM 順序在前、先畫，
    //  加上對話框有 z-index: 1，所以是框疊在上面不會被蓋掉。
    //  （往上溢出不需要 spill：spill 是給下緣讓空間用的。）
    spill: "5%",
    //  上下跟前後拉開（Kevin 2026-10-21）。
    //
    //  ⭐ 上緣的門檻是算出來的：那題框的 top 是 −12%，圖高 753px →
    //  它掛在圖上緣之上 90px。pad 小於 90 的話框就吃進上一塊的範圍（試過 60px，差 30px）。
    //  100px → 框頂落在上一塊下緣下方 10px。
    pad: "100px",
    bubbles: [
      {
        text: "情線如\n八字交織",
        //  字比示意圖小很多（0.23 → 0.19 → 0.165，Kevin 2026-10-21 兩輪）。
        top: "-12%",
        left: "7%",
        width: "35%",
        ratio: 0.165,
        aspect: "0.75",
      },
      {
        text: "有些相遇\n本就是前世\n欠下的\n命中注定",
        top: "63%",
        left: "42%",
        //  同上：0.189 → 0.155 → 0.135。
        width: "50%",
        ratio: 0.135,
        aspect: "0.71",
        glow: true,
      },
    ],
  },

  //32. 命書面板④
  {
    type: "panel",
    bg: imgUrl("feihong-story-bg-3.jpg"),
    title: "那人的身影",
    bodyFrom: "person",
  },

  //33.
  {
    type: "text",
    //  字級與上下留白都是量示意圖來的（Kevin 2026-10-21 的 149.png），
    //  換算成 420px 欄寬下的 CSS px：
    //    讓本妖為你撥開這桃花霧  26.7px  #b3b3b3 漸層、粗體  → fade bold lg
    //                             （示意圖是 26.7px，但 Kevin 要再大一點）
    //    把手給我…              30.6px  白、粗體              → strong flat xxl
    //    你將會找到 那個人        28.6px  白 + rgb(199,42,77)     → strong flat xl
    //                             （後兩行也是 Kevin 要求比示意圖再大一級）
    //  區塊上緣 → 第一列墨跡 64px → head-sm（54px）。
    //  下緣量到至少 162px（示意圖在那裡就裁掉了，數字是下界）→ tail-xl（130px）。
    head: "sm",
    tail: "xl",
    lines: [
      { text: "讓本妖為你撥開這桃花霧", style: "fade bold lg" },
      { text: "把手給我…", style: "strong flat xxl", gap: "lg" },
      { parts: ["你將會找到 ", { text: "那個人" }], style: "strong flat xl" },
    ],
  },

  //34.
  //  量示意圖（Kevin 2026-10-21 的 150.png），圖欄 899px、原檔 1500×1028：
  //    框 383×520  left 10.6%  top 52.8%  width 42.6%  寬高比 0.737
  //    文字寬 352 ÷ 一行 5 字 = 70px ÷ 框寬 383 = ratio 0.184
  //    框底 1010 − 圖底 781 = 229px = 圖寬的 25%
  //  沒有粉光（量到 0 點）。
  //
  //  ⭐ 量法要注意：橢圓裡有字，直接拿「最寬的一列」會抳到被字切斷的列。
  //  要看整條 y 方向的寬度分佈再取其中的最大值（這張是 y=720 的 384px）。
  {
    type: "image",
    src: imgUrl("feihong-story-12.png"),
    //  比示意圖再往下、字再小（Kevin 2026-10-21，兩輪）：
    //  top 53 → 62 → 70%、ratio 0.184 → 0.155 → 0.14 → 0.15。spill 跟著重算：
    //  框高 = 181 ÷ 0.74 = 244，框底 = 288×70% + 244 = 446，
    //  (446 − 圖高 288) ÷ 圖寬 420 = 38%。
    spill: "38%",
    bubbles: [
      {
        text: "指尖紅線\n早已刻下了\n你的情途",
        top: "70%",
        left: "11%",
        width: "43%",
        ratio: 0.15,
        aspect: "0.74",
      },
    ],
  },

  //35. 導購段開始：從「她看到了什麼」轉成「報告裡有什麼」。
  //  字級量示意圖（Kevin 2026-10-21 的 151.png）：兩行都是 31.2 CSS px → xl。
  //
  //  ⭐「非凡愛情宿命」是**漸層字**（part.style: "gradient"）——
  //  跟「是你還沒看懂八字牽下的紅線」同一組漸層（白 → #6e1228），
  //  只是用在一行裡的幾個字。
  {
    type: "text",
    head: "lg",
    tail: "xl",
    lines: [
      { text: "緋紅還想告訴你", style: "strong flat xl" },
      {
        parts: ["只屬於你的", { text: "非凡愛情宿命", style: "gradient" }],
        style: "strong flat xl",
      },
    ],
  },

  //36.
  //  ⭐「你命裡的桃花正要盛開」從**獨立的文字 block**改成**疊在圖上的字幕**
  //  （Kevin 2026-10-21 的 152.png）。舊版是圖下面另一塊黑底的字。
  //
  //  量示意圖（圖欄 899px、原檔 1500×1140）：
  //    字幕頂 ÷ 圖高 = 28.0%（實際排上去 Kevin 要垂直置中 → center: true）
  //    字級 28.9 CSS px（Kevin 要再大一點 → xl，30.5px）
  //    筆畫四周有紅光：3px (89,23,34)、12px 還有 (75,20,28) → is-strong
  {
    type: "image",
    src: imgUrl("feihong-story-13.png"),
    captions: [
      { center: true, lines: [{ text: "你命裡的桃花正要盛開", style: "strong xl" }] },
    ],
  },

  //38. ⚠️「萬字」是賣出去的承諾 —— 報告引擎的總字數要對得上這個數字。
  //    白夜鏡月賣的是五萬字（九章二十七節），緋紅是六卷，量級不同。
  //  量示意圖（Kevin 2026-10-21 的 153.png），換算成 420px 欄寬下的 CSS px：
  //    描述你的萬字愛情報告      28.9px  白、粗體、**無光**
  //    一字一句皆由八字定奪      29.0px  同上
  //    （分隔線）                 高 139px、色 rgb(202,43,78)
  //    你的命格、你的正緣、你的情劫  22.3px  白、粗體、無光
  //
  //  ⭐ 三行都是 flat（關光）—— 量示意圖筆畫四周離 3px 就是 (0,0,0)。
  //  ⭐ 三行合成**一塊**（舊版是兩塊，而且沒有分隔線）——
  //  示意圖上它們是同一組，中間靠線分開，跟另外兩條故事線的結構一致。
  {
    type: "text",
    head: "lg",
    tail: "lg",
    lines: [
      { text: "描述你的萬字愛情報告", style: "strong flat lg" },
      { text: "一字一句皆由八字定奪", style: "strong flat lg" },
      { divider: "long" },
      { text: "你的命格、你的正緣、你的情劫", style: "strong flat sm" },
    ],
  },

  //40.
  //  ⚠️ 這一顆的數字是**目測估的**，不是量的 ——
  //  Kevin 2026-10-21 那張示意圖是內嵌在訊息裡的，沒落到檔案，量不到像素。
  //  其他有圖檔的題都是量出來的，這一顆要以實際畫面為準再調。
  //  目測：框在右下、約半個圖寬、字有粉光。
  {
    type: "image",
    src: imgUrl("feihong-story-14.png"),
    spill: "16%",
    bubbles: [
      {
        text: "再亂的結\n本妖都能找\n出一根主線",
        //  字比目測值小很多（0.17 → 0.14，Kevin 2026-10-21）。
        top: "74%",
        left: "41%",
        width: "50%",
        ratio: 0.14,
        aspect: "0.76",
        glow: true,
      },
    ],
  },

  //41.
  {
    type: "text",
    //  ⚠️ 字級是**目測估的** —— 示意圖內嵌在訊息裡、沒落到檔案，量不到像素。
    //  結構跟示意圖一致：三行都粗體無光，第二行最大，第三行是灰階漸層。
    head: true,
    tail: "md",
    lines: [
      { text: "不必開口", style: "strong flat lg" },
      { text: "已經看清你的紅線牽往何處", style: "strong flat xl", gap: true },
      { text: "又在哪裡打下了死結", style: "fade bold lg" },
    ],
  },

  //42-47. 六卷的章節卡。
  //
  //  ⚠️ 卷名與條目名是**付款前的承諾**，報告引擎的章節表要一字不差 ——
  //  使用者掏錢買的是「爛桃花的破局法則」，不是模型當下想到的別的說法。
  //  （白夜鏡月是九章二十七節，緋紅是六卷十八條 —— 量級不同，不要照抄。）
  //
  //  底圖六張都一樣（bg-2）：六卷是同一套東西，各配一張會看起來像六個產品。
  {
    type: "chapter",
    bg: imgUrl("feihong-story-bg-2.png"),
    badge: "第一卷",
    title: "感情性格與相處模式",
    items: [
      { label: "日主本命總覽", text: "緋紅指尖撥開迷霧，八字五行日主為你寫下的情感原貌" },
      { label: "夫妻宮與日支暗湧", text: "干支生剋定數，決定你在親密關係裡的真性情與情愛底色" },
      { label: "十神煞氣與心牆", text: "你骨子裡壓不住的保護色、感情防衛機制與專屬戀愛天賦" },
    ],
  },
  {
    type: "chapter",
    bg: imgUrl("feihong-story-bg-2.png"),
    badge: "第二卷",
    title: "紅線劃出你喜歡的類型",
    items: [
      { label: "偏官與傷官的致命誘惑", text: "八字喜用神揭密，你內心深處最無法抗拒的靈魂特質" },
      { label: "眼緣與氣場的弦外之音", text: "你以為的擇偶標準 vs. 八字喜神真正渴望的補完對象" },
      { label: "無意識的淪陷時刻", text: "當哪種五行氣場靠近時，會瞬間瓦解你的理智與防線" },
    ],
  },
  {
    type: "chapter",
    bg: imgUrl("feihong-story-bg-2.png"),
    badge: "第三卷",
    title: "磁場下你實際吸引的對象",
    items: [
      //  ⭐ 原本是「桃花星與**紅艷殺**的牽引」—— 八字引擎沒有紅艷殺。
      //  analyze() 交出來的神煮只有庫／馬／花三星與空亡，沒有正式的神煮表。
      //  改成「日支」：那是八字的夫妻宮，在四柱裡本來就有（含藏干與十神），
      //  講「你實際吸引誰」比神煮穩。（Kevin 2026-10-21 決定改文案、不補引擎。）
      { label: "桃花星與日支的牽引", text: "你身上的八字氣場，究竟在瘋狂招惹哪些紅塵過客" },
      { label: "孽緣還是良緣", text: "為什麼你總是吸引到某種讓你心累、卻又欲罷不能的人" },
      { label: "爛桃花的破局法則", text: "看清吸煞體質，如何揮劍斬斷那些消耗你能量的無效情緣" },
    ],
  },
  {
    type: "chapter",
    bg: imgUrl("feihong-story-bg-2.png"),
    badge: "第四卷",
    title: "未來正緣個性分析",
    items: [
      { label: "夫妻宮正緣畫像", text: "天干地支顯像，命中注定那人的性格、處事風格與內涵" },
      { label: "相處模式與靈魂契合", text: "當正緣降臨，你們會是互補包容、溫柔相守還是歡喜冤家" },
      { label: "正緣的顯赫特質與細節", text: "他的年齡差距、事業氣場，以及能給你帶來極大安全感的關鍵細節" },
    ],
  },
  {
    type: "chapter",
    bg: imgUrl("feihong-story-bg-2.png"),
    badge: "第五卷",
    title: "未來感情走勢與桃花時機",
    items: [
      { label: "大運與流年紅線引動", text: "八字官星與財星降臨之時，你的正緣將在何時渡海而來" },
      //  ⭐ 原本是「季節與**方位**」（2026-10-22 改）。
      //  方位技術上推得出來（喜用神五行 → 東南西北），但那句話承諾的是
      //  「去哪裡會遇到人」—— 那是鐵口直斷、無法驗證，而且閻異閣賣的是看透不是法術。
      //  改成「徵兆」：講的是那段緣分靠近時你身上會先出現什麼，
      //  依據一樣是流年與大運，而且是可以回頭對照的。
      { label: "桃花盛開的季節與徵兆", text: "緋紅指點迷津，揭示你命中註定與他相遇的時機與轉折點" },
      { label: "破除單身魔咒的關鍵時刻", text: "歲月如流沙，如何抓住這波最強的情緣大運成功脫單" },
    ],
  },
  {
    type: "chapter",
    bg: imgUrl("feihong-story-bg-2.png"),
    //tail：六卷列完了，下一塊是她的收口，中間要斷開
    tail: true,
    badge: "第六卷",
    title: "感情裡的潛在挑戰與風險",
    items: [
      { label: "刑沖破害的情劫危機", text: "夫妻宮逢沖逢害之年，關係中最容易爆發的衝突與考驗" },
      { label: "感情中的致命盲點", text: "八字過旺或過弱帶來的性格硬傷，小心親手推開愛你的人" },
      { label: "緋紅的破局指南", text: "如何在情劫降臨時轉危為安，讓這一段牽絆能抵擋得過歲月漫長" },
    ],
  },

  //48. 六卷列完之後的收口：她說這次不用猜。
  {
    type: "image",
    src: imgUrl("feihong-story-15.png"),
    //  量示意圖（Kevin 2026-10-21 的 156.png），圖欄 899px、原檔 1500×2668：
    //    框 437×575  left 40.4%  top 69.9%  width 48.6%  寬高比 0.760
    //    文字寬 406 ÷ 最長一行 6 字 = 68px ÷ 框寬 437 = ratio 0.155
    //    框底 1751 − 圖底 1658 = 93px = 圖寬的 10%
    //  字有粉光（量到 28011 個偏粉的點）。
    //
    //  實際排上去 Kevin 要框再長、字再小、位置再下（2026-10-21）：
    //    aspect 0.76 → 0.68（框 206×271 → 206×303）
    //    ratio  0.155 → 0.14
    //    top    70% → 76%
    //  spill 跟著重算：框底 = 747×76% + 303 = 871，(871−747)÷420 = 30%。
    spill: "30%",
    bubbles: [
      {
        text: "這一次\n我們不猜了\n本妖直接告訴\n你答案",
        top: "76%",
        left: "40%",
        width: "49%",
        ratio: 0.14,
        aspect: "0.68",
        glow: true,
      },
    ],
  },

  //49. 進入用戶回饋段的標頭。
  {
    type: "text",
    head: "lg",
    lines: [
      //  flat：「閻異閣-緋紅」要純白，不帶紅光（Kevin 2026-10-21）。
      //  舊值是 strong sm —— strong 自帶紅光，所以要加 flat 關掉。
      { text: "閻異閣-緋紅", style: "strong flat lg" },
      { text: "真實用戶回饋", style: "gradient xxl" },
    ],
  },

  //50. 用戶回饋。信箱是遮罩過的，評語裡的換行照使用者原本打的斷。
  { type: "reviews", items: REVIEWS },
];

//—— 三條線各一份 ——
//  ⚠️ 尚未交往／已交往那兩條還沒做，暫時共用單身線這一份。
//  那兩條有兩張盤（命盤區塊要並排兩份）、主詞是「你們」，
//  而且「破除單身魔咒」那類句子對他們沒有意義 —— 不能一直借。

//—— 尚未交往線的故事頁 ——
//
//  問診走 pair 路徑、關係狀態選「曖昧還沒交往」的人。
//  跟單身線的差別不只是換字：
//    兩張盤    命盤區塊畫兩張（你的＋另一位的），文案主詞是「你們」
//    七卷      單身線六卷。多出來的是「剖析對方」與「他對感情的態度」——
//              有了對方的八字才寫得出來
//    三塊面板  單身線四塊。這條線沒有「那人的身影」—— 那個人已經在眼前了
//    兩個大字  宿命／時機。沒有「正緣」—— 正緣是還沒遇到的人才用的詞
//
//  圖沿用同一組（都是緋紅本人，沒有指涉單身與否）。
const BLOCKS_AMBIGUOUS = [
  //  ⭐ 版型全部對齊單身線（2026-10-21）。
  //  兩條線用的是**同一組圖**，所以對話框的位置、框寬、寬高比、spill
  //  可以整組照搬 —— 那些數字是量單身線的示意圖來的，換一條線不會變。
  //  會變的只有兩種：
  //    1. 框裡的字數不同（最長那一行變長了）→ ratio 要降，不然會撞到框邊
  //    2. 文字 block 的句子比較長（「你們」比「你」多一個字）→ 要加 wide
  //  每一處偏離單身線的值都在下面註明原因。

  //1-2. 開場。跟單身線同一張圖、同一句話 —— 這兩塊是她的招牌，三條線共用。
  { type: "image", src: imgUrl("feihong-story-1.png") },
  //  上下留得比別塊多 —— 這是整頁的第一句話，
  //  前後都是滿幅的圖，用預設的 50／30 會變成「圖縫裡夾了兩行字」。
  {
    type: "text",
    head: "lg",
    tail: "lg",
    lines: [
      "來看看這漫天飄落的桃花",
      { text: "為你纏繞了怎樣的情緣劇本", style: "gradient-soft" },
    ],
  },

  //3-4. 痛點換成曖昧的那一種：不是寂寞，是懸著。
  {
    type: "image",
    src: imgUrl("feihong-story-2.png"),
    bubbles: [{ text: "嗯…", top: "16%", left: "8%", width: "22%" }],
  },
  //  ⚠️ 紅字在**最後一行**，不是第一行（Kevin 2026-10-22 的 158.png）。
  //  跟單身線反過來：那邊是「獨自一個人多久了？」當大紅標、下面三句白字接著；
  //  這條線是兩句白字鋪陳（試探、猜忌…／這一段酸甜交織的情緣），
  //  最後一句才落下來。曖昧的痛點是「懸著」，要先描述那個懸、再問結果。
  //
  //  量 158.png（欄寬 900px，換算成 420px 欄寬下的 CSS px）：
  //
  //    試探、猜忌、忽遠忽近…   24.8px  白            → smd（24.8px）
  //    這一段酸甜交織的情緣     24.8px  白            → smd
  //    究竟是良緣還是劫數？     字距 32.2px  rgb(204,43,79)  → jade-deep md
  //
  //  ⭐ 前兩行走的是新補的 is-smd（宣告 26px）。
  //  量到的 24.8px 正好落在 sm（排出來 23.9）與 md（26.7）中間：
  //  sm 偏小、md 偏大，兩輪都被退回，所以在中間補了一級（見 scss 的說明）。
  //
  //  ⭐ 第三行是量「相鄰兩個字的中心距」推出來的，不是量墨跡高 ——
  //  jade-deep 自帶 letter-spacing .2em，所以中心距 = 字級 × 1.2：
  //    md 在 420px 視窗是 26.7px → 26.7 × 1.2 = 32.0px，量到 32.2px，就是它。
  //    （lg 會是 34.3px，差了兩級以上，不是 lg。）
  //
  //  ⭐ 光暈是**只往下**的：量筆畫上下各 2～13px 的 R 中位數 ——
  //  往上 46→1（13px 就歸零），往下一路維持在 52。那正是 jade-deep 的
  //  `0.04em 0.16em 0.14em` 那道紅影，不是繞一圈的光。
  //
  //  gap 寫在第三行（0.8em = 21.4px），量到的空檔是 19.2px。
  //  ⚠️ 不要寫在第二行 —— has-gap 加的是 margin-top，寫在上一行是把它自己往下推。
  //
  //  不需要 wide：最長的第三行 10 字 × 26.7 × 1.2 + 縮排 5.3 = 325px，
  //  預設內距下可用 372px；320px 的舊機也只要 249px（可用 280px）。
  //  上下留白從預設的 50／60 加到 68／66（Kevin 2026-10-22「稍微再大一點點」）。
  //  head 與 tail 的級距是各自調的，沒有一組相等 —— 68／66 是兩邊最接近的一組。
  {
    type: "text",
    head: true,
    tail: "md",
    lines: [
      { text: "試探、猜忌、忽遠忽近…", style: "smd" },
      { text: "這一段酸甜交織的情緣", style: "smd" },
      { text: "究竟是良緣還是劫數？", style: "jade-deep md", gap: true },
    ],
  },

  //5-6. 四柱。位置與尺寸照搬單身線（量示意圖來的，見 BLOCKS_SINGLE 的註解）。
  {
    type: "image",
    src: imgUrl("feihong-story-3.png"),
    bubbles: [
      {
        from: "pillars",
        top: "60%",
        left: "49%",
        width: "39%",
        ratio: 0.166,
        aspect: "0.73",
        glow: true,
      },
    ],
  },
  { type: "text", tail: true, lines: [{ from: "name", style: "name" }] },

  //7-8. 「別再一個人苦苦猜測」直接命中曖昧期最難受的地方：
  //     不是沒人愛，是不知道對方在想什麼。
  { type: "image", src: imgUrl("feihong-story-4.png") },
  //  照單身線：四行漸層字、中間一條 tight 的分隔線。
  //  前三句比 block 的字級大一級（md），最後那句最長所以留在 block 的 xs。
  //
  //  ⭐ 原本前三句是 is-mark（紅色色塊）—— 不能跟漸層字並存：
  //  is-gradient-soft 收在 #6e1228，色塊是 #8c0026，字的下半段會沒入底色。
  //  （單身線那塊走過同一條路，最後定案是只留漸層字。）
  {
    type: "text",
    size: "xs",
    wide: true,
    even: true,
    lines: [
      { text: "別再一個人苦苦猜測了", style: "gradient-soft md" },
      { text: "感情裡的試探與煎熬", style: "gradient-soft md" },
      { text: "最是消耗靈魂", style: "gradient-soft md" },
      { divider: "tight" },
      {
        text: "是為了看透「他心裡有沒有你」吧？",
        style: "gradient-soft",
        //  滑到這一句，底部的購買按鈕就出現（跟白夜、鏡月同一個位置）。
        id: CTA_TRIGGER_ID,
      },
    ],
  },

  //9-10. 這題框掛出圖片下緣，所以要給 spill（佔圖**寬**的百分比）。
  //  文字跟單身線一字不差，整組數字照搬。
  {
    type: "image",
    src: imgUrl("feihong-story-5.png"),
    spill: "32%",
    bubbles: [
      {
        text: "牽著這根\n緋紅絲線",
        top: "85%",
        left: "29%",
        width: "42%",
        ratio: 0.155,
        aspect: "0.76",
      },
    ],
  },
  //  ⭐ 比單身線多一行，而且第一句長（13 字）→ 要 wide。
  //  13 × 26.7（md 在 420px 視窗的實際值）= 347px，12px 內距下可用 396px。
  //
  //  上緣從預設的 50px 加到 68px（Kevin 2026-10-22「距離上方多一點點」）。
  //  ⚠️ 這一塊的上緣要從**橢圓底**量起，不是從圖的下緣 ——
  //  上一塊那顆「牽著這根緋紅絲線」掛出圖外 134px（spill 32%），
  //  而 spill 是 margin、這裡是 padding，兩者不合併、是相加的。
  //  量到的是 56px → 加完是 74px。
  //  （示意圖 159.png 量到 57.8px，等於示意圖本來就跟舊值一樣 ——
  //  這一次是要比示意圖再開一點，不是照示意圖補。）
  {
    type: "text",
    size: "sm",
    head: true,
    wide: true,
    lines: [
      { text: "本妖替你撥開這層曖昧的迷霧", style: "md" },
      { text: "看看你們之間", style: "strong md", gap: "sm" },
      { text: "究竟是真情還是錯覺", style: "strong md" },
    ],
  },

  //11. 命書面板①。內文由 AI 依兩人的八字生成（bodyFrom）。
  //  上緣的間距主要是面板自己的 padding（100px）撐的，外面只要 22px。
  {
    type: "panel",
    bg: imgUrl("feihong-story-bg-3.jpg"),
    head: "xs",
    title: "你現在的狀態是",
    bodyFrom: "situation",
  },

  //12. 把上面那段 AI 寫的東西歸因到命盤 —— 這一句是整頁的轉折。
  //  「卑微」是這條線專屬的詞 —— 單身線那邊寫的是「愛得這麼累」。
  //
  //  ⭐ 比單身線多一行：「終於明白你為何在這段關係裡如此卑微了」18 字，
  //  一行排不下（lg 在 420px 視窗是 28.6px，18 字要 515px），只能斷成兩行。
  {
    type: "text",
    head: "lg",
    tail: "lg",
    wide: true,
    lines: [
      { text: "看到這裡", style: "lg" },
      { text: "終於明白你為何在這段關係裡", style: "lg" },
      { text: "如此卑微了", style: "lg" },
      { parts: ["全是你的", { text: "命盤" }, "寫好的"], style: "strong flat lg", gap: "lg" },
    ],
  },

  //13-14. ⚠️ 合盤線要畫**兩張**命盤。
  //       第二張讀 partnerName / partnerBirthDate ——
  //       對方多半沒填名字，所以退到「另一位」（示意圖就是這樣寫的）。
  { type: "chart", bg: imgUrl("feihong-story-bg-1.png"), from: "chart" },
  {
    type: "chart",
    bg: imgUrl("feihong-story-bg-1.png"),
    from: "partnerChart",
    nameFrom: "partnerName",
    dateFrom: "partnerBirthDate",
    fallbackName: "另一位",
  },

  //15. 跟單身線一字不差，數字照搬（上 54px、下 66px，兩行都 lg）。
  {
    type: "text",
    head: "sm",
    tail: "md",
    lines: [
      { text: "那根纏在你指尖的命定紅線", style: "lg" },
      { text: "到底綁在誰的身上", style: "lg" },
    ],
  },

  //16. 兩題框都掛出圖的下緣，右那題掛得很多（spill 取掛最低的那一題）。
  //
  //  ⭐ 右那題的 ratio 比單身線小（0.161 → 0.14）：
  //  單身線最長一行是「窺見自己的」5 字，這條線是「窺見這段感情」6 字。
  //  照 0.161 排是 170px、框內寬只有 176px —— 橢圓在那個高度還沒到最寬處，
  //  字會壓到框線。0.14 排出來 148px，跟單身線的佔比（81%）一致。
  //  （框寬與 aspect 都沒動，所以 spill 不用重算。）
  {
    type: "image",
    src: imgUrl("feihong-story-6.png"),
    spill: "52%",
    bubbles: [
      {
        text: "指尖紅絲已\n動…",
        top: "54%",
        left: "11%",
        width: "30%",
        ratio: 0.182,
        aspect: "0.73",
      },
      {
        text: "你敢不敢\n窺見這段感情\n的最終定數",
        top: "93%",
        left: "45%",
        width: "42%",
        ratio: 0.14,
        aspect: "0.73",
        glow: true,
      },
    ],
  },

  //17. ⭐ 第三句 13 字（含一個半形空格）→ 要 wide，lg 在 420px 視窗排出來 386px。
  {
    type: "text",
    head: true,
    tail: "md",
    wide: true,
    lines: [
      { text: "看清了", style: "lg" },
      { text: "這根紅線在他指尖纏繞", style: "lg" },
      { text: "到底是在靠攏 還是在悄悄斷裂", style: "fade lg" },
    ],
  },

  //18-19. 先示弱再逼問。「你，真有膽量聽？」是鉤子 —— 問完就給答案。
  //  字級與色系照單身線（那組是量示意圖來的，見 BLOCKS_SINGLE）。
  { type: "image", src: imgUrl("feihong-story-7.png") },
  {
    type: "text",
    lines: [
      { text: "不過…本妖只剝開真相", style: "md" },
      { text: "可不負責替你擦眼淚", style: "md" },
      { text: "你，真有膽量聽？", style: "jade-strong", gap: true },
      { divider: true },
      { text: "緋紅這就一字一句", style: "strong flat xxl", gap: true },
      { text: "慢慢拆給你聽", style: "fade xxl bold" },
    ],
  },

  //20. 第一個大字。這條線只有兩個（宿命／時機）——
  //    「正緣」是還沒遇到的人才用的詞。
  {
    type: "text",
    tail: "lg",
    lines: [{ ornament: imgUrl("feihong-story-bg-4.jpg") }, { title: "宿\n命" }],
  },

  //21. 「破局期」是這條線的位置 —— 單身線是「情緣轉折期」。
  //
  //  ⭐ 左那題的 ratio 比單身線小（0.22 → 0.18）：
  //  單身線是「你現在」3 字，這條線是「你們現在」4 字。
  //  0.22 排出來 111px、框內寬 126px（88%），擠到框線了；
  //  0.18 是 91px（72%），跟單身線的佔比（66%）相當。
  {
    type: "image",
    src: imgUrl("feihong-story-8.png"),
    spill: "23%",
    bubbles: [
      {
        text: "你們現在\n正處於",
        top: "50%",
        left: "10%",
        width: "30%",
        ratio: 0.18,
        aspect: "0.73",
      },
      {
        text: "人生關鍵的\n破局期",
        top: "75%",
        left: "40%",
        width: "49%",
        ratio: 0.14,
        aspect: "0.73",
        glow: true,
      },
    ],
  },

  //22. 命書面板②
  //  ⭐ head: true（200px）不是 --head-sm（90px）—— **上下外距會合併**。
  //  前一塊的 spill 是圖寬的 23% = 97px 的 margin-bottom，
  //  相鄰時取最大值不是和：90px 等於沒加，200px 才真的空出 103px。
  {
    type: "panel",
    bg: imgUrl("feihong-story-bg-3.jpg"),
    head: true,
    title: "你的感情宿命\n是這樣流轉的",
    bodyFrom: "destiny",
  },

  //23.
  {
    type: "text",
    head: true,
    tail: "md",
    lines: [
      { text: "這桃花酒雖甜", style: "lg" },
      { text: "但若酒裡摻了毒", style: "lg" },
      { text: "本妖可不許你一飲而盡", style: "fade lg" },
    ],
  },

  //24.
  {
    type: "text",
    tail: "lg",
    lines: [{ ornament: imgUrl("feihong-story-bg-4.jpg") }, { title: "時\n機" }],
  },

  //25-28. 兩題框的字數跟單身線一樣（最長都是 5 字），整組數字照搬。
  {
    type: "image",
    src: imgUrl("feihong-story-9.png"),
    spill: "17%",
    bubbles: [
      {
        text: "那麼…",
        top: "45%",
        left: "10%",
        width: "30%",
        ratio: 0.22,
        aspect: "0.73",
      },
      {
        text: "你們的關係\n何時能突破\n僵局？",
        top: "72%",
        left: "40%",
        width: "49%",
        ratio: 0.14,
        aspect: "0.73",
      },
    ],
  },
  //  wide 是為了 320px 的舊機：11 字 × 25.8px = 284px，
  //  而 320−40（小螢幕內距）= 280px —— 差 4px 就折了。
  {
    type: "text",
    size: "sm",
    head: "lg",
    tail: "xl",
    wide: true,
    lines: [{ text: "看看紅線是怎麼說的吧？", style: "jade-strong" }],
  },
  { type: "image", src: imgUrl("feihong-story-10.png") },
  //  上緣比下緣多一級（88 vs 66px）—— 前一塊是圖，圖自己的下緣有暗場，
  //  上面要留得比下面多才看得出來。
  {
    type: "text",
    head: "lg",
    tail: "md",
    lines: [
      { text: "關係確立的機緣", style: "md" },
      { text: "即將浮現", style: "md" },
    ],
  },

  //29. 命書面板③（這條線沒有「那人的身影」—— 那個人已經在眼前了）
  {
    type: "panel",
    bg: imgUrl("feihong-story-bg-3.jpg"),
    title: "你們的情線\n是這樣流轉的",
    bodyFrom: "bond",
  },

  //30. 單身線那句是「破除單身魔咒」—— 曖昧的人要的不是脫單，是主導權。
  //  字級與字重照單身線那塊（量示意圖來的）：
  //    第一行 白、粗體、**無光**（flat）
  //    第二行 灰階漸層、粗體、小一級
  //    後兩行 白、粗體、**有紅光**（strong，不是紅色色塊）
  //  下緣兩輪加大（Kevin 2026-10-22）：66px → 90px（--tail-lg）→ 130px（--tail-xl）。
  //  墨跡底到下一塊跟著走 74 → 98 → 138px。
  //  ⚠️ 90 到 130 中間沒有級 —— 這一跳是 +40px，比前一跳（+24）大。
  //  示意圖 160.png 量到的是 75.9px，所以現在是示意圖的 1.8 倍，
  //  刻意比示意圖開得多（那句「逼出他真心的最佳時機」要沉一下再進下一張圖）。
  {
    type: "text",
    head: "lg",
    tail: "xl",
    lines: [
      { text: "緋紅色的花瓣", style: "strong flat xl" },
      { text: "正在夜空中綻放", style: "fade bold xl" },
      { text: "此時正是掌控主導權", style: "strong md", gap: "lg" },
      { text: "逼出他真心的最佳時機", style: "strong md" },
    ],
  },

  //31-34. 導購段：從「她看到了什麼」轉成「報告裡有什麼」。
  {
    type: "image",
    src: imgUrl("feihong-story-12.png"),
    spill: "38%",
    bubbles: [
      {
        text: "指尖紅線\n早已刻下了\n你的情途",
        top: "70%",
        left: "11%",
        width: "43%",
        ratio: 0.15,
        aspect: "0.74",
      },
    ],
  },
  //  ⭐「修成正果」「痛心轉身」是**漸層字**（part.style: "gradient"）——
  //  跟單身線的「非凡愛情宿命」同一組漸層（白 → #6e1228），用在一行裡的幾個字。
  {
    type: "text",
    head: "lg",
    tail: "xl",
    lines: [
      { text: "緋紅還想告訴你", style: "strong flat xl" },
      {
        parts: ["這段曖昧最終會", { text: "修成正果", style: "gradient" }],
        style: "strong flat xl",
      },
      {
        //  ⭐ 問號跟在**漸層那一段裡面**，不是另外一段 ——
        //  __mark 這類強調段是 inline-block（漸層字一定要，不然 background-clip
        //  會按整行算裁切範圍），而 inline-block 左右各有一截間距。
        //  把「？」拆成第三段的話，它跟「痛心轉身」之間會裂開一個空格，
        //  讀起來像打錯字。
        parts: ["還是讓你", { text: "痛心轉身？", style: "gradient" }],
        style: "strong flat xl",
      },
    ],
  },
  //  ⭐「你們之間的紅線／正在劇烈顫動」從**獨立的文字 block**改成**疊在圖上的字幕**
  //  （跟單身線的「你命裡的桃花正要盛開」同一個作法）。
  //  兩行不併成一行：13 字在 xl（420px 視窗 30.5px）要 396px，字幕沒有 wide 可用。
  {
    type: "image",
    src: imgUrl("feihong-story-13.png"),
    captions: [
      {
        center: true,
        lines: [
          { text: "你們之間的紅線", style: "strong xl" },
          { text: "正在劇烈顫動", style: "strong xl" },
        ],
      },
    ],
  },

  //34. ⚠️「萬字」是賣出去的承諾 —— 報告引擎的總字數要對得上這個數字。
  //  ⭐ 三行合成**一塊**（舊版是兩塊、而且沒有分隔線），跟單身線一致。
  //  ⭐ 三行都是 flat（關光）—— 單身線量示意圖時筆畫四周離 3px 就是 (0,0,0)。
  //  ⭐ 線下面是**兩行**不是一行（單身線是一行）：
  //  「你們的關係、他的真意、這段情的終點」17 個字，sm 在 420px 視窗是 23.9px
  //  → 406px，而 wide 的可用寬只有 396px，每一個寬度都會折。
  //  硬要排成一行就得降到 xs（19.1px），那一級在這個位置太小；
  //  斷成兩行反而跟上面兩行的節奏一致。
  //  （單身線那句「你的命格、你的正緣、你的情劫」只有 13 個字，一行排得下。）
  {
    type: "text",
    head: "lg",
    tail: "lg",
    wide: true,
    lines: [
      { text: "描述你的萬字曖昧合盤解析", style: "strong flat lg" },
      { text: "一字一句皆由二人八字定奪", style: "strong flat lg" },
      { divider: "long" },
      //  比示意圖大一級（Kevin 2026-10-22「稍微加大一點」）：
      //  161.png 量到 24.0／24.2px，等於 sm 排出來的 23.9px，
      //  也就是示意圖本來就跟舊值一樣；md 是 26.7px。
      //  （中間的 smd 只差 0.9px，看不出來，所以直接跳一級。）
      { text: "你們的關係、他的真意", style: "strong flat md" },
      { text: "這段情的終點", style: "strong flat md" },
    ],
  },

  //35-36.
  {
    type: "image",
    src: imgUrl("feihong-story-14.png"),
    spill: "16%",
    bubbles: [
      {
        text: "再亂的結\n本妖都能知\n道線的去向",
        top: "74%",
        left: "41%",
        width: "50%",
        ratio: 0.14,
        aspect: "0.76",
        glow: true,
      },
    ],
  },
  //  ⭐ 前兩句比單身線小一級（lg → md）：那邊最長 12 字，這邊 14 字。
  //  14 × 26.7（md 在 420px 視窗）= 374px，12px 內距下可用 396px。
  //  最後那句是落點，所以反過來大一級。
  {
    type: "text",
    head: true,
    tail: "md",
    wide: true,
    lines: [
      { text: "想知道他有沒有把你放在心上？", style: "strong flat md" },
      { text: "還是這只是你一個人的獨角戲？", style: "fade bold md" },
      { text: "紅線已經道盡一切", style: "strong flat xl", gap: "lg" },
    ],
  },

  //37-43. 七卷。
  //  ⚠️ 卷名與條目名是**付款前的承諾**，報告引擎的章節表要一字不差。
  //  （單身線六卷、這條線七卷 —— 多出來的是「剖析對方」與「他對感情的態度」，
  //  有了對方的八字才寫得出來。）
  //  底圖七張都一樣（bg-2）：七卷是同一套東西，各配一張會看起來像七個產品。
  {
    type: "chapter",
    bg: imgUrl("feihong-story-bg-2.png"),
    badge: "第一卷",
    title: "你可能喜歡的類型",
    items: [
      { label: "日主喜用與情愛偏好", text: "緋紅撥開五行迷霧，解析你八字深處最容易動心的靈魂特質" },
      { label: "十神牽引與擇偶盲點", text: "你以為的理想型 vs. 八字喜神真正能治癒你的氣場類型" },
      { label: "無意識的淪陷時刻", text: "當哪種五行特質出現時，會瞬間擊中你的心防讓你無法自拔" },
    ],
  },
  {
    type: "chapter",
    bg: imgUrl("feihong-story-bg-2.png"),
    badge: "第二卷",
    title: "剖析對方的個性分析",
    items: [
      { label: "對方日主與五行本性", text: "緋紅指尖引線，看穿他藏在溫柔（或冷漠）外表下的真實性格" },
      { label: "對方八字格局與內在渴望", text: "揭密他骨子裡的事業野心、安全感缺口與真實脾氣" },
      { label: "對方的面具與防禦機制", text: "他是在欲擒故縱，還是習慣在感情裡保持距離？" },
    ],
  },
  {
    type: "chapter",
    bg: imgUrl("feihong-story-bg-2.png"),
    badge: "第三卷",
    title: "他對感情的態度與行為",
    items: [
      { label: "對方八字夫妻宮與感情觀", text: "他對承諾是認真渴望，還是只想享受曖昧的甜頭" },
      { label: "他的主動與退縮邏輯", text: "八字十神運作下，他忽冷忽熱、未敢跨越那一步的背後真相" },
      { label: "真情實意 vs. 寂寞替代", text: "緋紅替你剖析，他對你的好究竟是獨一無二還是隨手為之" },
    ],
  },
  {
    type: "chapter",
    bg: imgUrl("feihong-story-bg-2.png"),
    badge: "第四卷",
    title: "你們互相吸引的原因",
    items: [
      { label: "二人八字干支合化", text: "你們之間那股莫名的吸引力，究竟是哪兩股五行能量在劇烈碰撞" },
      { label: "靈魂互補與前世宿劫", text: "是命局中的喜用神相互吸引，還是一場專門來渡你的情劫" },
      { label: "執念與羈絆的來源", text: "為什麼明明知道會受傷，你卻依然無法把目光從他身上移開" },
    ],
  },
  {
    type: "chapter",
    bg: imgUrl("feihong-story-bg-2.png"),
    badge: "第五卷",
    title: "你們的速配程度",
    items: [
      { label: "八字合盤契合度總覽", text: "天干地支五行生克，算出你們在靈魂與現實層面的速配指數" },
      { label: "三合六合與刑沖破害", text: "你們是天生一對的良緣，還是注定充滿考驗與折磨的孽緣" },
      { label: "走入現實的成功機率", text: "拋開曖昧濾鏡，你們的命格到底能不能順利支撐彼此走下去" },
    ],
  },
  {
    type: "chapter",
    bg: imgUrl("feihong-story-bg-2.png"),
    badge: "第六卷",
    title: "交往後的相處與衝突",
    items: [
      { label: "親密關係中的磨合危機", text: "一旦確定關係，雙方八字性格硬傷最容易在哪裡爆發引線" },
      { label: "價值觀與溝通的致命陷阱", text: "八字沖克帶來的相處盲點，小心熱戀期過後演變成無休止的內耗" },
      { label: "地支相刑與第三者風險", text: "防範未然，這段關係在未來最需要提防的感情危機與外在干擾" },
    ],
  },
  {
    type: "chapter",
    bg: imgUrl("feihong-story-bg-2.png"),
    //tail：七卷列完了，下一塊是她的收口，中間要斷開
    tail: true,
    badge: "第七卷",
    title: "緋紅的紅線指點總整理",
    items: [
      { label: "這段曖昧的最終定數", text: "緋紅為你梳理全局，給這段進退兩難的情緣一個明確的終極答案" },
      { label: "掌控主導權的破局密鑰", text: "如何在接下來的關鍵時機點出手，逼出他的真心或優雅轉身" },
      { label: "給小嬌客的最後寄語", text: "莫在迷霧中委屈自己，緋紅指尖的紅線只為值得的人盛開" },
    ],
  },

  //44. 七卷列完之後的收口：她說這次不用猜。
  //  文字跟單身線一字不差，整組數字照搬。
  {
    type: "image",
    src: imgUrl("feihong-story-15.png"),
    spill: "30%",
    bubbles: [
      {
        text: "這一次\n我們不猜了\n本妖直接告訴\n你答案",
        top: "76%",
        left: "40%",
        width: "49%",
        ratio: 0.14,
        aspect: "0.68",
        glow: true,
      },
    ],
  },

  //45-46. 用戶回饋（三條線共用同一批）。
  //  flat：「閻異閣-緋紅」要純白，不帶紅光（strong 自帶紅光，要加 flat 關掉）。
  {
    type: "text",
    head: "lg",
    lines: [
      { text: "閻異閣-緋紅", style: "strong flat lg" },
      { text: "真實用戶回饋", style: "gradient xxl" },
    ],
  },
  { type: "reviews", items: REVIEWS },
];

//—— 已交往線的故事頁 ——
//
//  問診走 pair 路徑、且 status 是 dating／married 的人（見 constant/
//  feihong-story-line）。手上有兩張盤，主詞是「你們」。
//
//  跟尚未交往線的差別（Kevin 2026-10-20 的示意圖）：
//    痛點不同  曖昧是「懸著」、這條是「磨損」—— 平淡、吵架、冷戰、無話可說。
//              主訴從「他心裡有沒有我」變成「我們撐不撐得過歲月漫長」
//    不問去留  已經在一起了，所以卷名寫的是相處與長期經營，不是追或放。
//              第五卷「破除情劫 如何穩定走長期」是這條線的重心
//    七卷      卷名全部換過，跟曖昧線沒有一卷相同
//    三塊面板  同曖昧線（situation／destiny／bond），沒有「那人的身影」
//    兩個大字  宿命／時機，同曖昧線
//
//  圖沿用同一組（都是緋紅本人，沒有指涉關係階段）。
const BLOCKS_TOGETHER = [
  //  ⭐ 版型對齊尚未交往線（2026-10-22）。三條線用的是**同一組圖**，
  //  所以對話框的位置、框寬、寬高比、spill 整組照搬。
  //  會變的只有一種：這條線的句子普遍**比較長**（「你們二人」「這段相守」
  //  都比曖昧線多字），所以好幾行的字級要降一級、或斷成兩行。
  //  每一處偏離的理由都在下面註明。

  //1-2. 開場。三條線共用的招牌。
  { type: "image", src: imgUrl("feihong-story-1.png") },
  {
    type: "text",
    head: "lg",
    tail: "lg",
    lines: [
      "來看看這漫天飄落的桃花",
      { text: "為你纏繞了怎樣的情緣劇本", style: "gradient-soft" },
    ],
  },

  //3-4. 痛點換成長期關係的那一種：不是不愛，是靠得近卻碰不到。
  {
    type: "image",
    src: imgUrl("feihong-story-2.png"),
    bubbles: [{ text: "嗯…", top: "16%", left: "8%", width: "22%" }],
  },
  //  結構同尚未交往線：兩句白字鋪陳，最後才落下那句紅的。
  //
  //  ⭐ 落點排**一行**（Kevin 2026-10-22「不需要斷行」）。
  //  原句是「心與心之間，卻隔了一面牆？」，逗號拿掉 —— 不是為了好看，是排得下排不下：
  //  jade-deep 自帶 letter-spacing .2em 與同值的 text-indent，
  //  所以整行寬 ≈ 字數 × 字級 × 1.2 + 0.2 字級。
  //    13 字（含逗號）× 26.7 × 1.2 + 5.3 = 422px —— 連 wide 的 396px 都塞不下
  //    12 字（不含）    × 26.7 × 1.2 + 5.3 = 390px —— 420px 的欄寬排得下
  //
  //  ⭐ 落點是 md，但這一塊的左右內距要收到 8px（wide: "lg"）——
  //  卡住的是 **320px 的舊機**，而且只差 2px：
  //    md 在小螢幕是 20.4px → 12 × 20.4 × 1.2 + 縮排 4.1 = 298px
  //    --wide（12px 內距）只讓出 296px → 折行（320px 那一輪的檢測真的抓到）
  //    --wide-lg（8px）讓出 304px → 過
  //
  //  ⚠️ 先試過降字級（smd，24.8px），排得下但落點就跟上面兩句同一級、
  //  大小上沒有落差了。收內距比降字級好 —— 那一行的重量是這一塊的重點。
  {
    type: "text",
    head: true,
    tail: "md",
    wide: "lg",
    lines: [
      { text: "平淡、吵架、冷戰、無話可說…", style: "smd" },
      { text: "是不是常覺得明明靠得那麼近", style: "smd" },
      { text: "心與心之間卻隔了一面牆？", style: "jade-deep md", gap: true },
    ],
  },

  //5-6. 四柱。位置與尺寸照搬（量示意圖來的，見 BLOCKS_SINGLE 的註解）。
  {
    type: "image",
    src: imgUrl("feihong-story-3.png"),
    bubbles: [
      {
        from: "pillars",
        top: "60%",
        left: "49%",
        width: "39%",
        ratio: 0.166,
        aspect: "0.73",
        glow: true,
      },
    ],
  },
  { type: "text", tail: true, lines: [{ from: "name", style: "name" }] },

  //7-8. 「內耗」是這條線專屬的詞 —— 曖昧線寫的是「試探與煎熬」。
  { type: "image", src: imgUrl("feihong-story-4.png") },
  //  ⭐ 原本前幾句是 is-mark（紅色色塊），不能跟漸層字並存：
  //  is-gradient-soft 收在 #6e1228、色塊是 #8c0026，字的下半段會沒入底色。
  //  （三條線都走過同一條路，最後定案都是只留漸層字。）
  //
  //  ⭐ 最後那句 17 個字，是整頁最長的一行 —— 它就是 is-xs 那一級當初被
  //  補出來的原因（見 scss）。這裡用 block 的 size: "xs"（排出來 22.9px），
  //  前三句各自寫 md 比它大一級。
  {
    type: "text",
    size: "xs",
    wide: true,
    even: true,
    lines: [
      { text: "別再獨自咽下那些委屈與疲憊了", style: "gradient-soft md" },
      { text: "長久相處裡的內耗", style: "gradient-soft md" },
      { text: "最是磨損靈魂", style: "gradient-soft md" },
      { divider: "tight" },
      { text: "是為了看清", style: "gradient-soft" },
      {
        text: "「你們到底能不能撐過歲月漫長」吧？",
        style: "gradient-soft",
        //  滑到這一句，底部的購買按鈕就出現（跟白夜、鏡月同一個位置）。
        id: CTA_TRIGGER_ID,
      },
    ],
  },

  //9-10. 這題框掛出圖片下緣，所以要給 spill（佔圖**寬**的百分比）。
  {
    type: "image",
    src: imgUrl("feihong-story-5.png"),
    spill: "32%",
    bubbles: [
      {
        text: "牽著這根\n緋紅絲線",
        top: "85%",
        left: "29%",
        width: "42%",
        ratio: 0.155,
        aspect: "0.76",
      },
    ],
  },
  //  ⚠️ 上緣要從**橢圓底**量起，不是從圖的下緣 —— 上一塊那顆掛出圖外
  //  134px（spill 32%），而 spill 是 margin、這裡是 padding，兩者相加不合併。
  //
  //  ⭐ 後兩句**不自己寫字級**，吃 block 的 size: "xs"（排出來 22.9px）——
  //  Kevin 2026-10-22 要這兩句一起縮小，而 sm（23.9）與 xs（19.1）中間
  //  本來就沒有級。改成讓它們一起吃 block 的字級，兩句就同一級、
  //  也不必為了這一次再補一級出來。
  //    看看你們二人之間       26.7 → 22.9px
  //    是漸行漸遠…相守        23.9 → 22.9px（15 字 × 22.9 = 344px，可用 396px）
  //  第一句維持自己寫的 md（26.7px），比後兩句大一級。
  //
  //  ⚠️ 上緣要從**橢圓底**量起，不是從圖的下緣 —— 上一塊那顆掛出圖外
  //  134px（spill 32%），而 spill 是 margin、這裡是 padding，兩者相加不合併。
  {
    type: "text",
    size: "xs",
    head: true,
    wide: true,
    lines: [
      { text: "本妖替你撥開這層關係的迷霧", style: "md" },
      //  gap 從 sm（0.3em）加到預設（0.8em）：6.9 → 18.3px
      //  （Kevin 2026-10-22「距離上方再多一點點」）。
      { text: "看看你們二人之間", style: "strong", gap: true },
      { text: "是漸行漸遠，還是劫數過後的相守", style: "strong" },
    ],
  },

  //11. 命書面板①。上緣的間距主要是面板自己的 padding（100px）撐的。
  {
    type: "panel",
    bg: imgUrl("feihong-story-bg-3.jpg"),
    head: "xs",
    title: "你現在的狀態是",
    bodyFrom: "situation",
  },

  //12. 「這段相守」—— 不問去留，只說累。
  {
    type: "text",
    head: "lg",
    tail: "lg",
    wide: true,
    lines: [
      { text: "看到這裡", style: "lg" },
      { text: "終於明白你為何在這段相守裡", style: "lg" },
      { text: "這麼累了", style: "lg" },
      { parts: ["全是你的", { text: "命盤" }, "寫好的"], style: "strong flat lg", gap: "lg" },
    ],
  },

  //13-14. 合盤線畫**兩張**命盤。第二張讀 partnerName / partnerBirthDate ——
  //       對方多半沒填名字，所以退到「另一位」。
  { type: "chart", bg: imgUrl("feihong-story-bg-1.png"), from: "chart" },
  {
    type: "chart",
    bg: imgUrl("feihong-story-bg-1.png"),
    from: "partnerChart",
    nameFrom: "partnerName",
    dateFrom: "partnerBirthDate",
    fallbackName: "另一位",
  },

  //15.
  {
    type: "text",
    head: "sm",
    tail: "md",
    lines: [
      { text: "那根纏在你指尖的命定紅線", style: "lg" },
      { text: "到底綁在誰的身上", style: "lg" },
    ],
  },

  //16. 兩題框都掛出圖的下緣，右那題掛得很多（spill 取掛最低的那一題）。
  //  右那題最長一行 6 字（窺見這段關係），跟曖昧線同形，ratio 照搬 0.14。
  {
    type: "image",
    src: imgUrl("feihong-story-6.png"),
    spill: "52%",
    bubbles: [
      {
        text: "指尖紅絲已\n動…",
        top: "54%",
        left: "11%",
        width: "30%",
        ratio: 0.182,
        aspect: "0.73",
      },
      {
        text: "你敢不敢\n窺見這段關係\n的最終定數",
        top: "93%",
        left: "45%",
        width: "42%",
        ratio: 0.14,
        aspect: "0.73",
        glow: true,
      },
    ],
  },

  //17. ⭐ 末句排**一行**（Kevin 2026-10-22「不需要斷行」），但要降一級：
  //  「到底是更加緊扣 還是在悄悄斷裂」14 個字加一個半形空格，
  //    lg（28.6px）→ 14.5 × 28.6 = 415px，連 wide 的 396px 都塞不下
  //    md（26.7px）→ 387px
  //  前兩句維持 lg，所以這一塊的三行有大小差 —— 末句本來就是淡出的收尾。
  {
    type: "text",
    head: true,
    tail: "md",
    wide: true,
    lines: [
      { text: "看清了", style: "lg" },
      { text: "這根紅線纏繞了這麼多年", style: "lg" },
      { text: "到底是更加緊扣 還是在悄悄斷裂", style: "fade md" },
    ],
  },

  //18-19. 先示弱再逼問。字級與色系照單身線（那組是量示意圖來的）。
  { type: "image", src: imgUrl("feihong-story-7.png") },
  {
    type: "text",
    lines: [
      { text: "不過…本妖只剝開真相", style: "md" },
      { text: "可不負責替你擦眼淚", style: "md" },
      { text: "你，真有膽量聽？", style: "jade-strong", gap: true },
      { divider: true },
      { text: "緋紅這就一字一句", style: "strong flat xxl", gap: true },
      { text: "慢慢拆給你聽", style: "fade xxl bold" },
    ],
  },

  //20. 這條線只有兩個大字（宿命／時機）——
  //    「正緣」是還沒遇到的人才用的詞。
  {
    type: "text",
    tail: "lg",
    lines: [{ ornament: imgUrl("feihong-story-bg-4.jpg") }, { title: "宿\n命" }],
  },

  //21. 「磨合轉折期」是這條線的位置 —— 曖昧線是「破局期」。
  //  左那題「你們現在」4 字，跟曖昧線同形，ratio 照搬 0.18
  //  （單身線的「你現在」只有 3 字，用的是 0.22）。
  {
    type: "image",
    src: imgUrl("feihong-story-8.png"),
    spill: "23%",
    bubbles: [
      {
        text: "你們現在\n正處於",
        top: "50%",
        left: "10%",
        width: "30%",
        ratio: 0.18,
        aspect: "0.73",
      },
      {
        text: "人生關鍵的\n磨合轉折期",
        top: "75%",
        left: "40%",
        width: "49%",
        ratio: 0.14,
        aspect: "0.73",
        glow: true,
      },
    ],
  },

  //22. 命書面板②
  //  ⭐ head: true（200px）不是 --head-sm（90px）—— **上下外距會合併**。
  //  前一塊的 spill 是圖寬的 23% = 97px 的 margin-bottom，
  //  相鄰時取最大值不是和：90px 等於沒加，200px 才真的空出 103px。
  {
    type: "panel",
    bg: imgUrl("feihong-story-bg-3.jpg"),
    head: true,
    title: "你的感情宿命\n是這樣流轉的",
    bodyFrom: "destiny",
  },

  //23. 「喝久了難免發苦」—— 酒的比喻沿用，但換成已經喝了很久的版本。
  //  ⭐ 末句降一級（lg → md）：14 個字，lg 排出來 400px、超過 wide 的 396px。
  {
    type: "text",
    head: true,
    tail: "md",
    wide: true,
    lines: [
      { text: "這紅塵的酒喝久了難免發苦", style: "lg" },
      { text: "但若這杯酒已經變質", style: "lg" },
      { text: "本妖可不許你委屈自己硬喝下去", style: "fade md" },
    ],
  },

  //24.
  {
    type: "text",
    tail: "lg",
    lines: [{ ornament: imgUrl("feihong-story-bg-4.jpg") }, { title: "時\n機" }],
  },

  //25-28. 問的是「何時能重拾熱情」，不是「何時能突破僵局」。
  //  兩題框的字數跟曖昧線一樣（最長 5 字），整組數字照搬。
  {
    type: "image",
    src: imgUrl("feihong-story-9.png"),
    spill: "17%",
    bubbles: [
      {
        text: "那麼…",
        top: "45%",
        left: "10%",
        width: "30%",
        ratio: 0.22,
        aspect: "0.73",
      },
      {
        text: "你們的關係\n何時能重拾\n熱情？",
        top: "72%",
        left: "40%",
        width: "49%",
        ratio: 0.14,
        aspect: "0.73",
      },
    ],
  },
  //  wide 是為了 320px 的舊機：11 字 × 25.8px = 284px，
  //  而 320−40（小螢幕內距）= 280px —— 差 4px 就折了。
  {
    type: "text",
    size: "sm",
    head: "lg",
    tail: "xl",
    wide: true,
    lines: [{ text: "看看紅線是怎麼說的吧？", style: "jade-strong" }],
  },
  { type: "image", src: imgUrl("feihong-story-10.png") },
  //  上緣比下緣多一級（88 vs 66px）—— 前一塊是圖，圖自己的下緣有暗場，
  //  上面要留得比下面多才看得出來。
  {
    type: "text",
    head: "lg",
    tail: "md",
    lines: [
      { text: "關係回溫的機緣", style: "md" },
      { text: "即將降臨", style: "md" },
    ],
  },

  //29. 命書面板③
  {
    type: "panel",
    bg: imgUrl("feihong-story-bg-3.jpg"),
    title: "你們的情線\n是這樣流轉的",
    bodyFrom: "bond",
  },

  //30. 三句都是承諾：主導權、重整秩序、防範情劫 —— 在一起的人要的是守住。
  //  字級與字重照尚未交往線那塊（量示意圖來的）：
  //    第一行 白、粗體、**無光**（flat）
  //    第二行 灰階漸層、粗體、小一級
  //    後三行 白、粗體、**有紅光**（strong，不是紅色色塊）
  //  下緣 130px（--tail-xl）：那幾句承諾要沉一下再進下一張圖。
  {
    type: "text",
    head: "lg",
    tail: "xl",
    lines: [
      { text: "緋紅色的花瓣", style: "strong flat xl" },
      { text: "正在夜空中綻放", style: "fade bold xl" },
      //  比示意圖大一級（Kevin 2026-10-22「稍微加大」）：
      //  165.png 量到 27.0 / 27.0 / 26.9px，等於 md 排出來的 26.7px，
      //  也就是示意圖本來就跟舊值一樣。lg 是 28.6px。
      //  ⚠️ 不再往上 —— 上面兩行是 xl（30.5px），再大一級就齊平、沒有層次了。
      { text: "此時正是掌控主導權", style: "strong lg", gap: "lg" },
      { text: "重整感情秩序", style: "strong lg" },
      { text: "防範情劫的最佳時機", style: "strong lg" },
    ],
  },

  //31-33. 導購段：從「她看到了什麼」轉成「報告裡有什麼」。
  {
    type: "image",
    src: imgUrl("feihong-story-12.png"),
    spill: "38%",
    bubbles: [
      {
        text: "指尖紅線\n早已刻下了\n你的情途",
        top: "70%",
        left: "11%",
        width: "43%",
        ratio: 0.15,
        aspect: "0.74",
      },
    ],
  },
  //  ⭐「攜手白頭」「分道揚鑣」是**漸層字**（part.style: "gradient"）。
  //  ⚠️ 問號跟在漸層那一段**裡面**，不是另外一段 —— 強調段是 inline-block
  //  （漸層字一定要，不然 background-clip 會按整行算裁切範圍），
  //  拆成第三段的話中間會裂開一個空格，讀起來像打錯字。
  {
    type: "text",
    head: "lg",
    tail: "xl",
    lines: [
      { text: "緋紅還想告訴你", style: "strong flat xl" },
      {
        parts: ["這段感情最終是", { text: "攜手白頭", style: "gradient" }],
        style: "strong flat xl",
      },
      {
        parts: ["還是注定", { text: "分道揚鑣？", style: "gradient" }],
        style: "strong flat xl",
      },
    ],
  },
  //  ⭐ 這兩句是**疊在圖上的字幕**，不是獨立的黑底文字塊。
  {
    type: "image",
    src: imgUrl("feihong-story-13.png"),
    captions: [
      {
        center: true,
        lines: [
          { text: "你們之間的紅線", style: "strong xl" },
          { text: "正在劇烈顫動", style: "strong xl" },
        ],
      },
    ],
  },

  //34. ⚠️「萬字」是賣出去的承諾 —— 報告引擎的總字數要對得上這個數字。
  //  ⭐ 四行合成**一塊**（舊版是兩塊、而且沒有分隔線）。
  //  ⭐ 都是 flat（關光）—— 量示意圖筆畫四周離 3px 就是 (0,0,0)。
  {
    type: "text",
    head: "lg",
    tail: "lg",
    wide: true,
    lines: [
      { text: "描述你的萬字愛情合盤解析", style: "strong flat lg" },
      { text: "一字一句皆由二人八字定奪", style: "strong flat lg" },
      { divider: "long" },
      { text: "你們的關係、他的真意", style: "strong flat md" },
      { text: "這段情的終點", style: "strong flat md" },
    ],
  },

  //35-36. 「死結」—— 曖昧線問的是有沒有，這條線問的是卡在哪。
  {
    type: "image",
    src: imgUrl("feihong-story-14.png"),
    spill: "16%",
    bubbles: [
      {
        text: "再亂的結\n本妖都能知\n道線的去向",
        top: "74%",
        left: "41%",
        width: "50%",
        ratio: 0.14,
        aspect: "0.76",
        glow: true,
      },
    ],
  },
  //  落點在中間那句（跟單身線同形：短／長／淡出）。
  //  wide 是為了 320px：12 字 × 23.1px（xl 在小螢幕）= 277px，可用 280px。
  {
    type: "text",
    head: true,
    tail: "md",
    wide: true,
    lines: [
      { text: "不必開口", style: "strong flat lg" },
      { text: "已經看清你的紅線牽往何處", style: "strong flat xl", gap: true },
      { text: "又在哪裡打下了死結", style: "fade bold lg" },
    ],
  },

  //37-43. 七卷。
  //  ⚠️ 卷名與條目名是**付款前的承諾**，報告引擎的章節表要一字不差。
  //  底圖七張都一樣（bg-2）：七卷是同一套東西，各配一張會看起來像七個產品。
  {
    type: "chapter",
    bg: imgUrl("feihong-story-bg-2.png"),
    badge: "第一卷",
    title: "雙方個性分析",
    items: [
      { label: "雙方日主本命對照", text: "緋紅指尖撥開五行迷霧，剖析你們二人各自的八字本性與真實脾氣" },
      { label: "夫妻宮干支生克", text: "你看不到的性格硬傷 vs. 他藏在內心深處的固執與防禦機制" },
      { label: "八字格局的能量碰撞", text: "當強勢遇到隨和，抑或硬碰硬，看清彼此骨子裡的相處天性" },
    ],
  },
  {
    type: "chapter",
    bg: imgUrl("feihong-story-bg-2.png"),
    badge: "第二卷",
    title: "溝通模式與互動方式",
    items: [
      { label: "天干相合與對話磁場", text: "八字十神運作下，你們平時傳遞訊息與表達愛意的真實習慣" },
      { label: "語言偽裝與情緒接收器", text: "為什麼你說的是關心、他聽起來卻是挑剔？剖析溝通斷層的根源" },
      { label: "默契契合度與相處密碼", text: "八字五行如何互相補足，找出讓彼此待在一起最舒服的互動頻率" },
    ],
  },
  {
    type: "chapter",
    bg: imgUrl("feihong-story-bg-2.png"),
    badge: "第三卷",
    title: "可能的吵架點分析",
    items: [
      { label: "夫妻宮逢沖引發的爆點", text: "緋紅指點迷津，揭密你們最常為了哪些雞毛蒜皮大發雷霆" },
      { label: "八字忌神與情緒爆發引線", text: "當對方踩到你的五行地雷，兩人在冷戰與暴怒背後的真實心態" },
      { label: "相刑相害的情感內耗", text: "防範未然，看清哪些重覆發生的衝突正在悄悄磨損你們的感情" },
    ],
  },
  {
    type: "chapter",
    bg: imgUrl("feihong-story-bg-2.png"),
    badge: "第四卷",
    title: "彼此的感情真實需求",
    items: [
      { label: "你的八字安全感來源", text: "緋紅替你發聲，你內心深處最渴望被他理解、包容與呵護的脆弱點" },
      { label: "他的八字渴望與精神依託", text: "剖析他表面裝作不在乎、實則極度希望你給予的肯定與陪伴" },
      { label: "靈魂互補的供需法則", text: "拋開表面爭執，看清你們這段關係真正不可或缺的精神支柱" },
    ],
  },
  {
    type: "chapter",
    bg: imgUrl("feihong-story-bg-2.png"),
    badge: "第五卷",
    title: "破除情劫 如何穩定走長期",
    items: [
      { label: "流年大運的磨合轉折點", text: "當感情面臨平淡期或外在風波，八字氣場如何助你們化險為夷" },
      { label: "五行生克轉化秘訣", text: "緋紅給予的破局指南，如何在日常生活與氣場上為這段關係補運" },
      { label: "攜手白頭的避坑法則", text: "提前預防第三者干擾或財務危機，讓這一段牽絆能抵擋得過歲月漫長" },
    ],
  },
  {
    type: "chapter",
    bg: imgUrl("feihong-story-bg-2.png"),
    badge: "第六卷",
    title: "你們的戀愛合拍指數",
    items: [
      { label: "八字合盤契合度總覽", text: "天干地支五行生克，算出你們在靈魂、生活與現實層面的合拍指數" },
      { label: "三合六合的宿命羈絆", text: "你們到底是天造地設的良緣，還是需要經歷重重磨難的情劫" },
      { label: "走向未來的終極定數", text: "拋開生活濾鏡，你們的命格到底能不能穩定支撐彼此相守到老" },
    ],
  },
  {
    type: "chapter",
    bg: imgUrl("feihong-story-bg-2.png"),
    //tail：七卷列完了，下一塊是她的收口，中間要斷開
    tail: true,
    badge: "第七卷",
    title: "緋紅的紅線指點總整理",
    items: [
      { label: "這段牽絆的最終解答", text: "緋紅為你梳理婚姻與交往全局，給你們的未來一個明確的終極方向" },
      { label: "重拾熱情與和解的密鑰", text: "如何在接下來的關鍵時機點出手，撫平過往傷痕、重整感情秩序" },
      { label: "給相守兩人的最後寄語", text: "紅塵萬丈相遇不易，莫讓一時氣話斷了這根得來不易的緋紅絲線" },
    ],
  },

  //44. 七卷列完之後的收口：她說這次不用猜。
  {
    type: "image",
    src: imgUrl("feihong-story-15.png"),
    spill: "30%",
    bubbles: [
      {
        text: "這一次\n我們不猜了\n本妖直接告訴\n你答案",
        top: "76%",
        left: "40%",
        width: "49%",
        ratio: 0.14,
        aspect: "0.68",
        glow: true,
      },
    ],
  },

  //45-46. 用戶回饋（三條線共用同一批）。
  //  flat：「閻異閣-緋紅」要純白，不帶紅光（strong 自帶紅光，要加 flat 關掉）。
  {
    type: "text",
    head: "lg",
    lines: [
      { text: "閻異閣-緋紅", style: "strong flat lg" },
      { text: "真實用戶回饋", style: "gradient xxl" },
    ],
  },
  { type: "reviews", items: REVIEWS },
];

const BLOCKS_BY_LINE = {
  [STORY_LINE.SINGLE]: BLOCKS_SINGLE,
  [STORY_LINE.AMBIGUOUS]: BLOCKS_AMBIGUOUS,
  [STORY_LINE.TOGETHER]: BLOCKS_TOGETHER,
};

//對話框裡的字佔框寬的幾成（預設值）。
//  框放大之後字若照同一個比例跟著長，會把橢圓塞滿、少了留白 ——
//  0.29 讓「嗯…」在框裡浮著，四周留得開。
//
//  字多的框要自己覆寫（bubble.ratio）：同樣是一顆橢圓，
//  放兩個字和放四行三個字，能用的字級差一倍。
const BUBBLE_TEXT_RATIO = 0.29;

//把「框寬佔圖的百分比」換算成字級。
//  兩者都以整張圖為基準（container-type 在 .feihong-story__frame 上），
//  所以 15% 的框配 15 × 0.29 ≈ 4.4cqw 的字。
//  框改大小時字自動跟著，不必兩個數字一起調。
const bubbleFontSize = (width, ratio = BUBBLE_TEXT_RATIO) =>
  `${parseFloat(width) * ratio}cqw`;

//畫一行字。文字 block 與疊在圖上的字幕共用 ——
//  兩邊的一行都支援同樣的東西（style、gap、parts、from、divider、
//  ornament、title），抽出來才不會改了一邊忘了另一邊。
const renderLine = (line, i, data) => {
  //分隔線不是文字，不套 __line 那套字級與顏色 ——
  //  放在 lines 裡是因為它的位置就是「第幾行之後」，
  //  另外開一個欄位的話，改順序時兩邊都要記得動。
  //章節大標：三層同一組字疊在一起（左右暗分身 + 中間亮的）。
  //  分身用 aria-hidden：讀屏只該唸到一次「大運」。
  if (line.title)
    return (
      <span key={`title-${i}`} className="feihong-story__title">
        {["l", "r"].map((side) => (
          <span
            key={side}
            className={`feihong-story__title-layer feihong-story__title-layer--${side}`}
            aria-hidden="true"
          >
            {line.title}
          </span>
        ))}
        <span className="feihong-story__title-layer feihong-story__title-layer--core">
          {line.title}
        </span>
      </span>
    );

  //飾紋：一條橫的裝飾圖。跟 divider 一樣是「不是字，但位置是第幾行」
  if (line.ornament)
    return (
      <img
        key={`ornament-${i}`}
        className="feihong-story__ornament"
        src={line.ornament}
        alt=""
        loading="lazy"
      />
    );

  //divider: true 是預設（青綠），給字串就是變體（目前只有 "plain"）
  if (line.divider)
    return (
      <span
        key={`divider-${i}`}
        className={[
          "feihong-story__divider",
          line.divider !== true && `feihong-story__divider--${line.divider}`,
        ]
          .filter(Boolean)
          .join(" ")}
      />
    );

  //每一行可以只給字串（一般白字），或給物件帶樣式。
  //  兩種寫法並存是刻意的 —— 大多數行都是白字，
  //  全部都寫成 { text: "…" } 只是讓表變難讀。
  const { text, style, gap, from } =
    typeof line === "string" ? { text: line } : line;

  //from 的行內容要等資料（暱稱、四柱），作法同對話框的 bubble.from。
  //  BLOCKS 是模組層的常數，元件拿到 intake 之前就建好了，
  //  所以表裡只能記「要哪一個欄位」，值在這裡才查得到。
  //parts：一行裡有局部換色的重點字（見 block 12）。
  //  拆成陣列而不是在字串裡塞標記（例如 **命格**）——
  //  那需要一個剖析器，而整份資料表只有這一種強調，不值得。
  const content = line.parts
    ? line.parts.map((part, j) =>
        typeof part === "string" ? (
          part
        ) : (
          //  part 可以自帶樣式（part.style）—— 現在只有 "band"：
          //  那是「這幾個字底下墊一條漸層色帶」，跟預設的「只改字色」是兩回事。
          <span
            className={[
              "feihong-story__mark",
              part.style && `feihong-story__mark--${part.style}`,
            ]
              .filter(Boolean)
              .join(" ")}
            key={`${j}-${part.text}`}
          >
            {part.text}
          </span>
        ),
      )
    : from
      ? data?.[from]
      : text;

  return (
    <p
      key={from ? `${from}-${i}` : `${text || "parts"}-${i}`}
      //給底部按鈕當觸發點用（見 CTA_TRIGGER_ID）。
      //  用 id 而不是 ref：renderLine 是一般函式不是元件，
      //  要掛 ref 得把整條渲染路徑改成元件才行。
      id={line.id}
      className={[
        "feihong-story__line",
        //style 可以給多個，用空白分隔（例如 "strong lg"）——
        //  一行的「顏色與字重」和「大小」是兩件事，
        //  每一種組合各開一個 class 的話會變成排列組合。
        style &&
          style
            .split(" ")
            .map((n) => `is-${n}`)
            .join(" "),
        //和上一行之間空一段。gap: true 是一整段的停頓，
        //  gap: "sm" 是同一口氣裡鬆一點的換行。
        gap && (gap === true ? "has-gap" : `has-gap-${gap}`),
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {content}
    </p>
  );
};

//畫一個 block。
//  版型判斷收在這裡，不寫進 BLOCKS 那張表 ——
//  表只描述「有什麼內容」，這裡才決定「長什麼樣」。
//
//  data 是「要等 API 才有的內容」（目前只有四柱）。
//  刻意用具名欄位查表（bubble.from）而不是把值直接塞進 BLOCKS：
//  BLOCKS 是模組層的常數，在元件還沒拿到 intake 之前就建好了。
const renderBlock = (block, index, data) => {
  const key = `${block.type}-${index}`;

  if (block.type === "image") {
    //一律包一層 frame：有對話框時它是定位基準，沒有時也統一結構 ——
    //  兩種寫法並存的話，接縫與 first-child 的樣式都要各寫一份。
    //框裡的對話框可以掛出圖片下緣（見 block 9）。溢出來的那一截是「浮」的，
    //  不佔版面高度，所以要自己把那段空間讓出來，不然會壓在下一塊上。
    //
    //  空間讓在這一塊的下緣、而不是叫下一塊多留上緣：
    //  那一截是這張圖的對話框造成的，之後把下一塊換掉或刪掉，
    //  它仍然需要同一段空間 —— 責任留在製造溢出的人身上才不會失聯。
    //
    //  百分比的 margin 是以「包含區塊的寬度」為基準（同 padding 那個坑），
    //  所以 spill 給的是「佔圖寬的百分比」，不是圖高。
    return (
      <div
        key={key}
        className="feihong-story__frame"
        //  spill 是「框掛出圖外要讓出的空間」，pad 是「這一塊跟前後要拉開」。
        //  兩者都落在下緣時要相加，不是取其一 ——
        //  否則「加了留白」會把「讓出來的空間」吃掉，框就壓到下一塊。
        //
        //  ⭐ 圖片 block 預設沒有上下留白（圖跟圖要接得像同一張長圖），
        //  所以 pad 是特例、不是通則：只給「前後都是文字、需要全報」的那幾塊。
        style={
          block.spill || block.pad
            ? {
                marginTop: block.pad || undefined,
                marginBottom:
                  block.spill && block.pad
                    ? `calc(${block.spill} + ${block.pad})`
                    : block.spill || block.pad,
              }
            : undefined
        }
      >
        <img
          className="feihong-story__image"
          src={block.src}
          alt=""
          //第一張要盡快出現（它就是第一屏），其餘等捲到附近再載
          loading={index === 0 ? "eager" : "lazy"}
        />

        {/* 疊在圖上的字幕：沒有框，整條橫跨、置中。
            位置只給 top —— 旁白的重點是「在畫面的第幾分之幾高」。 */}
        {(block.captions || []).map((caption, c) => (
          //  center: true → 垂直置中（top: 50% + translateY(-50%)）。
          //  不用在資料裡算一個百分比：那要扶掉字幕自己的高度，
          //  而字級會跟著欄寬變（見 &__line 的 min(px, vw)）—— 算出來的值只對一個寬度。
          <div
            key={`caption-${c}`}
            className={[
              "feihong-story__caption",
              caption.center && "feihong-story__caption--center",
            ]
              .filter(Boolean)
              .join(" ")}
            style={caption.center ? undefined : { top: caption.top }}
          >
            {caption.lines.map((line, i) => renderLine(line, i, data))}
          </div>
        ))}

        {/* 一張圖可以有好幾顆對話框（block 15 有兩顆）。
            順序就是 DOM 順序，位置各自由資料給，彼此不影響。 */}
        {(block.bubbles || []).map((bubble, b) => (
          <div
            key={`bubble-${b}`}
            className={[
              "feihong-story__bubble",
              //光暈是特例不是通則：判語那顆量到完全沒有色偏。
              //  true 是青藍，給字串就是別的顏色（目前只有 "jade" 綠）。
              bubble.glow &&
                (bubble.glow === true
                  ? "feihong-story__bubble--glow"
                  : `feihong-story__bubble--glow-${bubble.glow}`),
            ]
              .filter(Boolean)
              .join(" ")}
            //位置與寬度是每張圖各自的，寫在資料裡而不是樣式裡。
            //  字級也設在這一層（不是裡面的 span）—— 框的內距用 em，
            //  而 em 只認元素自己的 font-size（見 scss 裡的 padding）。
            style={{
              top: bubble.top,
              left: bubble.left,
              width: bubble.width,
              fontSize: bubbleFontSize(bubble.width, bubble.ratio),
              //沒給就讓 scss 用預設的 7/10
              ...(bubble.aspect ? { "--bubble-aspect": bubble.aspect } : null),
            }}
          >
            {/* 固定文案直接給 text，要等 API 的給 from。
                還沒回來時橢圓先空著 —— 這時候放「載入中」反而更吵，
                框本身已經是畫面的一部分，突然冒出來才是閃動。 */}
            <span className="feihong-story__bubble-text">
              {bubble.from ? data?.[bubble.from] : bubble.text}
            </span>
          </div>
        ))}
      </div>
    );
  }

  if (block.type === "chart") {
    const chart = data?.[block.from];
    //排盤還沒回來（或失敗）就整塊不畫 —— 空表格比沒有表格更像壞掉
    if (!chart?.columns?.length) return null;

    //藏干一柱最多三個。不足時用 null 補在「左邊」——
    //  這張表整體是由右往左讀的（年柱在最右），藏干也一樣：
    //  本氣要落在最右邊，所以陣列先反轉再往左補空格。
    const hidden = (list) => {
      const rev = [...list].reverse();
      return [...Array(Math.max(0, 3 - rev.length)).fill(null), ...rev];
    };

    const cols = chart.columns;

    return (
      <section
        key={key}
        className="feihong-story__chart"
        style={{ "--panel-bg": `url(${block.bg})` }}
      >
        <div className="feihong-story__chart-inner">
          {/* 標題的來源可以換（nameFrom / dateFrom）——
              合盤要畫兩張表：你的那張讀 name/birthDate，
              對方那張讀 partnerName/partnerBirthDate。
              對方多半沒填名字，所以 fallbackName 頂著（示意圖寫「另一位」）。 */}
          {(data?.[block.nameFrom || "name"] || block.fallbackName) && (
            <h2 className="feihong-story__chart-title">
              {data?.[block.nameFrom || "name"] || block.fallbackName} 的感情八字命盤
              {data?.[block.dateFrom || "birthDate"] && (
                <span className="feihong-story__chart-date">
                  {data[block.dateFrom || "birthDate"].replace(
                    /^(\d+)\/(\d+)\/(\d+)$/,
                    "$1年 $2月 $3日",
                  )}
                </span>
              )}
            </h2>
          )}

          {/* 欄數寫成 CSS 變數：不知時辰的人只有三柱，寫死 4 會破版 */}
          <div
            className="feihong-story__chart-grid"
            style={{ "--cols": cols.length }}
          >
            {/* 天干的十神。
                刻意沒有欄標題列（時柱／日柱…）也沒有列標題欄（十神／天干…）——
                四柱表的欄列順序是固定的讀法，看得懂的人不需要標籤，
                而標籤會把一張只有八個字的表撐成一張報表。 */}
            {cols.map((c) => (
              <div className="feihong-story__chart-cell" key={`g-${c.label}`}>
                {c.god}
              </div>
            ))}

            {/* 天干 / 地支：兩列結構一樣，只差取哪個欄位 */}
            {["gan", "zhi"].map((key) => (
              <Fragment key={key}>
                {cols.map((c) => (
                  <div
                    className={`feihong-story__chart-cell feihong-story__chart-glyph feihong-story__chart-glyph--${c[key].element}`}
                    key={`${key}-${c.label}`}
                  >
                    {c[key].char}
                  </div>
                ))}
              </Fragment>
            ))}

            {/* 藏干，以及它們各自的十神。
                十神是直書的（兩個字上下疊）—— 一柱最多三個十神並排，
                橫著寫的話六個字會擠成一長條，分不出哪兩個字是一組。 */}
            {["char", "god"].map((field) => (
              <Fragment key={field}>
                {cols.map((c) => (
                  <div
                    className="feihong-story__chart-hidden"
                    key={`${field}-${c.label}`}
                  >
                    {hidden(c.hidden).map((h, i) => (
                      <span
                        className={[
                          "feihong-story__chart-hidden-cell",
                          field === "god"
                            ? "feihong-story__chart-hidden-cell--god"
                            : null,
                          h && field === "char"
                            ? `feihong-story__chart-hidden-cell--${h.element}`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        key={`${c.label}-${field}-${i}`}
                      >
                        {h ? h[field] : ""}
                      </span>
                    ))}
                  </div>
                ))}
              </Fragment>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (block.type === "reviews") {
    return (
      <section key={key} className="feihong-story__reviews">
        {block.items.map((review, i) => (
          <article
            className="feihong-story__review"
            key={`${i}-${review.email}`}
          >
            <p className="feihong-story__review-email">{review.email}</p>
            {/* 星星用字元重複而不是圖示：它要跟文字一起縮放、一起繼承顏色。
                aria-label 才是給讀屏的，★ 本身標成 aria-hidden，
                不然會被唸成五次「黑星」。 */}
            <p
              className="feihong-story__review-stars"
              aria-label={`${review.stars} 顆星`}
            >
              <span aria-hidden="true">{"★".repeat(review.stars)}</span>
            </p>
            <p className="feihong-story__review-text">{review.text}</p>
          </article>
        ))}
      </section>
    );
  }

  if (block.type === "chapter") {
    return (
      <section
        key={key}
        className={[
          "feihong-story__chapter",
          //跟後面那一塊拉開（章與章之間另有 & + & 管）
          block.tail && "feihong-story__chapter--tail",
        ]
          .filter(Boolean)
          .join(" ")}
        style={{ "--panel-bg": `url(${block.bg})` }}
      >
        <div className="feihong-story__chapter-inner">
          {block.badge && (
            <p className="feihong-story__chapter-badge">{block.badge}</p>
          )}
          {block.title && (
            <h2 className="feihong-story__chapter-title">{block.title}</h2>
          )}

          {(block.items || []).map((item, i) => (
            <p className="feihong-story__chapter-item" key={`${i}-${item.label}`}>
              {/*  ♥（實心愛心）而不是 ◆ —— Kevin 2026-10-21 的示意圖。
                   U+2665 不是 emoji，是文字符號：跟著內文的字色與字級走，
                   不會在不同系統上變成彩色的圖案。 */}
              <span className="feihong-story__chapter-mark" aria-hidden="true">
                ♥
              </span>
              {item.label}：{item.text}
            </p>
          ))}
        </div>
      </section>
    );
  }

  if (block.type === "panel") {
    //內容可以是陣列（一段一筆）或一整串換行分隔的字。
    //  容忍兩種是因為 AI 回來的是 string[]，但寫死在表裡的可能是一坨文字。
    const body = block.bodyFrom ? data?.[block.bodyFrom] : block.body;
    const paragraphs = Array.isArray(body)
      ? body
      : String(body || "")
          .split("\n")
          .map((t) => t.trim())
          .filter(Boolean);

    //沒有內文就整塊不掛。
    //  掛一個只有標題、底下一片空白的黑卡會比少掉這一塊更像壞掉 ——
    //  而這一頁是連續往下捲的長圖，中間少四塊看不出來。
    //
    //  這同時涵蓋兩種情況：AI 還在路上、以及 AI 生成失敗。
    //  使用者分不出差別，所以不必分開處理。
    if (!paragraphs.length) return null;

    return (
      <section
        key={key}
        className={[
          "feihong-story__panel",
          //跟文字 block 同名的旋鈕：上緣多留一截，跟前一塊拉開。
          //  true 是預設，給字串就是分級（目前只有 "sm"）。
          block.head &&
            (block.head === true
              ? "feihong-story__panel--head"
              : `feihong-story__panel--head-${block.head}`),
        ]
          .filter(Boolean)
          .join(" ")}
        //背景圖寫在 inline style：哪一塊用哪張圖是內容的事，不是樣式的事。
        //  用 CSS 變數而不是直接設 backgroundImage —— 花紋實際畫在 ::before 上
        //  （那一層才做上下淡出），而偽元素沒辦法用 inline style 指定。
        style={{ "--panel-bg": `url(${block.bg})` }}
      >
        <div className="feihong-story__panel-card">
          {block.title && (
            <h2 className="feihong-story__panel-title">{block.title}</h2>
          )}

          {paragraphs.map((text, i) => (
            <p
              className="feihong-story__panel-text"
              key={`${i}-${text.slice(0, 8)}`}
            >
              {text}
            </p>
          ))}
        </div>
      </section>
    );
  }

  if (block.type === "text") {
    return (
      <div
        key={key}
        className={[
          "feihong-story__text-block",
          //放大是整段的事，所以掛在 block 上而不是逐行掛 ——
          //  掛在行上的話，同一段裡加一行就得記得也標一次。
          block.size && `feihong-story__text-block--${block.size}`,
          //講完要停一下的段落：下緣多留一大截，下一塊圖才不會緊接著上來。
          //  tail: true 是預設，給字串就是分級（目前只有 "lg"）——
          //  作法同行距的 gap，級距集中在 scss 裡，資料表只說「要哪一級」。
          block.tail &&
            (block.tail === true
              ? "feihong-story__text-block--tail"
              : `feihong-story__text-block--tail-${block.tail}`),
          //重新開口的段落：上緣多留一截，跟前一塊拉開。
          //  true 是預設，給字串就是分級（目前只有 "lg"）。
          block.head &&
            (block.head === true
              ? "feihong-story__text-block--head"
              : `feihong-story__text-block--head-${block.head}`),
          //上下等距。預設的 50／30 是刻意不對稱的（下一塊多半是圖，
          //  圖自己的上緣就帶著一截暗場），但「整塊就是一段獨白」的
          //  block 要的是四周一樣寬的留白 —— 那用 head/tail 湊不出來，
          //  那兩組的級距是各自調的（68／60、88／90），沒有一組相等。
          //上下等距。true 是預設（54px），給字串就是分級（目前只有 "lg" = 80px）。
          block.even &&
            (block.even === true
              ? "feihong-story__text-block--even"
              : `feihong-story__text-block--even-${block.even}`),
          //左右內距收窄，把寬度讓給字（見 scss 的 --wide）。
          //true 是 12px，給字串就是分級（目前只有 "lg" = 8px，再收一截）。
          block.wide &&
            (block.wide === true
              ? "feihong-story__text-block--wide"
              : `feihong-story__text-block--wide-${block.wide}`),
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {block.lines.map((line, i) => renderLine(line, i, data))}
      </div>
    );
  }

  return null;
};

const FeihongStoryPage = () => {
  //—— 動線打點：進故事頁、讀到底 ——
  //
  //  「讀到底」用捲動判斷，不在 JSX 裡放哨兵元素 ——
  //  這一頁的內容是 BLOCKS 資料表畫出來的，塞一個跟內容無關的收尾節點
  //  會讓那張表之外多出一個要記得維護的東西。
  //
  //  打完就把監聽拿掉：捲動事件在手機上一秒觸發幾十次，而這件事
  //  只需要知道一次。留 120px 的餘裕 —— 行動瀏覽器的網址列收合會讓
  //  底部差個幾十 px，要求剛好捲到底的話很多人永遠觸發不了。
  useEffect(() => {
    track("story_view", "match-feihong");

    const onScroll = () => {
      const el = document.documentElement;
      if (el.scrollTop + el.clientHeight < el.scrollHeight - 120) return;
      track("story_end", "match-feihong");
      window.removeEventListener("scroll", onScroll);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navigate = useNavigate();
  //捲動容器。按鈕的觸發判斷要以它為基準 ——
  //  這一頁自己捲（見 scss 的 .feihong-story），不是整個視窗在捲，
  //  IntersectionObserver 的 root 給錯就永遠不會觸發。
  const scrollRef = useRef(null);
  //付款輪詢的計時器。放 ref 是為了離開這一頁時清掉 ——
  //  留著的話它會在元件卸載之後繼續打 API，而且永遠不會停。
  const pollRef = useRef(null);
  const [showCta, setShowCta] = useState(false);
  const [buying, setBuying] = useState(false);

  const { state, search } = useLocation();

  const intake =
    state?.intake || (wantsSampleIntake(search) ? sampleIntakeFor(search) : null);

  //這一份問診走哪一條故事線。三條線各有自己的 BLOCKS（見檔案上方）——
  //  單身線只有一張盤，另外兩條是合盤，文案與命盤區塊都不一樣。
  //  ⚠️ 一定要排在 intake 宣告之後 —— const 不會提升。
  //  intake 還沒到（直接貼網址進來）時 storyLineOf 會退到單身線。
  const blocks = BLOCKS_BY_LINE[storyLineOf(intake)] || BLOCKS_SINGLE;


  //—— 購買流程 ——
  //
  //  購買 sheet（選方案）→ 發票 sheet（載具／統編）→ 建單 → 跳轉綠界。
  //  中間不擋登入：這是單次消費的商品，使用者剛走完十二題問診，
  //  在這裡擋一道登入牆是會掉單的。身分靠發票填的那個信箱
  //  （見後端 P-4b 與 models/order 的 email 欄位說明）。
  //
  //  方案跟後端拿，不寫死在前端 —— 建單時算的金額必須跟畫面上顯示的
  //  是同一份。兩邊各存一份價格，遲早會出現「畫面寫一個數、實際扣另一個」。
  const [plans, setPlans] = useState([]);
  //選完方案、等著填發票的那一份。null 代表發票 sheet 沒開。
  const [pendingPlan, setPendingPlan] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [payError, setPayError] = useState("");

  //  ⚠️ 要帶 line：緋紅的商品名稱隨故事線不同（見 config/product-plans
  //  的 labelByLine）。不帶的話單身線的人會在購買視窗看到
  //  「緋紅 八字合婚・你們之間那條線」—— 他根本還沒有對象。
  const storyLine = storyLineOf(intake);
  useEffect(() => {
    PaymentService.plans("report", storyLine)
      .then(({ data }) => {
        if (!data?.status) return;
        //後端回的是 { id, name, summary, price, original, discount }，
        //  PurchaseSheet 要的是 { id, title, note, price, original } ——
        //  在這裡轉，不要去改那支共用元件的介面（提問頁也在用它）。
        //
        //  ⚠️ 要先濾掉別支產品的方案。/plans?type=report 回的是**所有**
        //  報告類方案（現在有緋紅與鏡月兩支），不濾的話緋紅的購買視窗
        //  會同時列出兩份，使用者可以在這一頁買到另一支產品。
        //  濾的是方案 id，對應 config/product-plans.js 的鍵。
        setPlans(
          (data.data?.plans || [])
            .filter((p) => p.id === "feihong")
            .map((p) => ({
              id: p.id,
              title: p.name,
              note: p.summary,
              price: p.price,
              original: p.original,
            })),
        );
      })
      .catch(() => {
        //拿不到方案時購買 sheet 會是空的。不另外跳錯誤 ——
        //  使用者還在看故事，這時彈一個「載入失敗」只是打斷他。
        //  真的按下購買才會發現，那時 PurchaseSheet 自己會顯示沒有方案。
      });
    //  依賴 storyLine：直接貼網址進來時 intake 還沒到，storyLineOf 會先
    //  退到單身線，等 state 進來才是真的那一條 —— 不重抓的話，
    //  已交往的人會停在「八字姻緣」那個名字上。
  }, [storyLine]);

  //購買 sheet 按下「繼續」：收起它、打開發票 sheet
  const handlePickPlan = useCallback(({ plan }) => {
    setPayError("");
    setPendingPlan(plan);
    setBuying(false);
  }, []);

  //—— 盯著訂單，等它變成已付款 ——
  //
  //  綠界開在新分頁之後，這一頁就與付款流程脫鉤了：
  //  付款結果是綠界 server-to-server 送到後端的，瀏覽器收不到任何通知，
  //  而導回 /purchase-result 的是「那一個分頁」，不是這一頁。
  //  所以這一頁只能自己問後端：這張單付了沒。
  //
  //  用輪詢而不是等分頁回報（postMessage / 監聽關閉）：
  //  使用者可能直接把綠界那頁關掉、可能在手機上切走再也沒切回來，
  //  那些情況下回報永遠不會來，但訂單其實已經付掉了。
  //  問後端是唯一不依賴使用者行為的做法。
  const watchOrder = useCallback(
    (orderID) => {
      if (!orderID) return;
      clearInterval(pollRef.current);

      const started = Date.now();
      pollRef.current = setInterval(() => {
        //超過上限就停。不設上限的話，使用者放著不管就是一個
        //  每三秒打一次、永遠不會停的請求迴圈。
        if (Date.now() - started > PAY_POLL_LIMIT_MS) {
          clearInterval(pollRef.current);
          return;
        }

        PaymentService.orderStatus(orderID)
          .then(({ data }) => {
            const status = data?.data?.status;
            if (process.env.NODE_ENV === "development") {
              console.log(`[ECPay] 查單 ${orderID} → ${status || "查無"}`);
            }
            if (status !== "paid") return;
            clearInterval(pollRef.current);
            //付掉了就把這一頁也帶到結果頁 —— 綠界那個分頁已經在那裡了，
            //  兩邊一致，使用者關掉哪一個都不會看到一個停在半路的畫面。
            navigate(`/purchase-result?orderID=${orderID}`, { replace: true });
          })
          //查單失敗不中斷輪詢：可能只是網路斷一下，下一輪就回來了
          .catch(() => {});
      }, PAY_POLL_MS);
    },
    [navigate],
  );

  //離開這一頁就停掉輪詢
  useEffect(() => () => clearInterval(pollRef.current), []);

  //發票填完：建單並跳轉綠界
  const handleCheckout = useCallback(
    (invoice) => {
      if (!pendingPlan || submitting) return;
      setSubmitting(true);
      setPayError("");

      //分頁要在這裡開 —— 這一行還在使用者按下按鈕的那個事件裡。
      //  等建單的 API 回來才開會被瀏覽器當成彈窗擋掉（理由見 ecpay-redirect）。
      const win = openEcpayWindow();

      //—— 動線打點：送出結帳 ——
      //  打在真的要建單之前。最後一段（付款成功）不打點 ——
      //  綠界導回時使用者可能已經關掉分頁，那個數字要從訂單來
      //  （見 config/funnel.js 的說明）。
      track("checkout_submit", "match-feihong");
      PaymentService.checkoutGuest(pendingPlan.id, invoice, intake)
        .then(({ data }) => {
          if (!data?.status) {
            setPayError(data?.message || "建立訂單失敗");
            win?.close();
            setSubmitting(false);
            return;
          }

          //—— 測試用：把這一單的編號印出來 ——
          //  綠界的付款結果是 server-to-server 送到後端的，瀏覽器永遠看不到，
          //  所以這裡只能給編號，讓人拿它去對後端 log 或查訂單狀態。
          //  正式環境不印：orderID 出現在 console 等於多一個外洩管道。
          if (process.env.NODE_ENV === "development") {
            const id = data.data.params?.MerchantTradeNo;
            console.log(
              `[ECPay] 建單成功 orderID=${id}\n` +
                `  綠界通知會打到後端，看 server 的 [ECPay] log\n` +
                `  或查：curl -s http://localhost:8080/api/payment/order-status/${id}`,
              data.data.params,
            );
          }

          submitToEcpay(data.data.action, data.data.params, win);
          //開在新分頁的話這一頁還在，submitting 要解除 ——
          //  不解除的話使用者在綠界那邊取消、切回來會發現按鈕永遠是暗的。
          if (win) {
            setSubmitting(false);
            watchOrder(data.data.params?.MerchantTradeNo);
          }
        })
        .catch((e) => {
          win?.close();
          setPayError(
            e?.response?.data?.message || "建立訂單失敗，請稍後再試",
          );
          setSubmitting(false);
        });
    },
    [pendingPlan, submitting, intake, watchOrder],
  );
  //沒帶 state 時：開發中或網址帶 ?preview 就用範例資料頂著，
  //  其餘情況維持原本行為 —— 沒有問診資料就導回劇情頁。


  //—— 四柱 ——
  //  排盤在後端（見 general-route 的 G-3）：四柱牽涉立春換年、節氣換月、
  //  子時跨日與真太陽時校正，而且畫面上這四柱必須跟之後報告裡的完全一致，
  //  所以跟報告共用同一支 getPillars，前端只負責顯示。
  //
  //  失敗就讓框空著，不擋流程 —— 這一格是氣氛，不是必要資訊，
  //  真正會用到四柱的是購買後那份報告，那時後端會自己重算一次。
  const [pillars, setPillars] = useState("");
  //命盤表的資料（十神／天干／地支／藏干）。跟四柱同一支 API 回來的。
  const [chart, setChart] = useState(null);
  //對方那張盤。只有合盤的兩條線（尚未交往／已交往）會有 ——
  //  單身線的人手上只有自己一張，這裡永遠是 null，那一塊就不掛。
  const [partnerChart, setPartnerChart] = useState(null);

  useEffect(() => {
    if (!intake) return undefined;

    //元件在請求回來前就被卸載（使用者按了返回）時，不要再 setState
    let alive = true;

    generalService
      .baziPillars({
        birthDate: intake.birthDate,
        birthTime: intake.birthTime,
        birthPlace: intake.birthPlace,
        gender: intake.gender,
      })
      .then(({ data }) => {
        if (!alive || !data?.status) return;
        //一柱一行，靠 .feihong-story__bubble-text 的 pre 斷行
        setPillars((data.data?.pillars || []).join("\n"));
        setChart(data.data || null);
      })
      .catch((e) => console.warn("[緋紅] 四柱取得失敗", e?.message));

    //合盤的兩條線要再排一次對方的盤。
    //  刻意是兩支獨立的請求、不合成一支：
    //  對方的生辰是使用者替他填的，缺時辰、地點填錯都很常見。
    //  合成一支的話，對方那半排不出來會連帶把使用者自己那張盤一起弄不見——
    //  而自己那張是整頁的主角（四柱對話框也要用）。
    if (isPairLine(intake)) {
      generalService
        .baziPillars({
          birthDate: intake.partnerBirthDate,
          birthTime: intake.partnerBirthTime,
          birthPlace: intake.partnerBirthPlace,
          gender: intake.partnerGender,
        })
        .then(({ data }) => {
          if (!alive || !data?.status) return;
          setPartnerChart(data.data || null);
        })
        .catch((e) =>
          console.warn("[緋紅] 對方四柱取得失敗", e?.message),
        );
    }

    return () => {
      alive = false;
    };
  }, [intake]);

  //—— 四塊命書面板的內文（AI 生的）——
  //
  //  請求不是在這裡發的，是劇情頁在問診倒數第二題就發了（見 feihong-teaser-cache）。
  //  這裡只是去把那個還在路上的 Promise 接回來 —— 從發出到使用者滑到
  //  第一塊面板，中間隔著兩支影片與整頁 15% 的捲動，約三十秒。
  //
  //  null 有兩種意思：還沒回來、或生成失敗。兩種的畫面是一樣的
  //  （那四塊整個不掛，見 renderBlock 的 panel 分支）——
  //  因為使用者分不出差別，而且這是付費前的鉤子不是商品，
  //  少掉四塊不該擋住整條動線。
  //開場那兩支影片播完了沒。沒播完就不讓底下的長捲頁出現 ——
  //  影片是蓋在上面的（fixed），但底下的頁面還是會吃捲動，
  //  不擋的話使用者可以一邊看影片一邊把故事滑掉。
  const [introDone, setIntroDone] = useState(false);

  //—— 四塊命書面板的內文（AI 生的）——
  //
  //  —— 為什麼是「按下開始解命」才發，不是一掛載就發 ——
  //  這支生成是付費前的成本：每個走到這裡的人都要燒一次，買不買都一樣。
  //  掛載就發的話，在第一支影片（9.5 秒）中途關掉頁面的人也會被算一次，
  //  那是純浪費。「按下開始解命」是一個明確的互動，意願訊號強得多。
  //
  //  時間還夠不夠：按下去之後還有 feihong-5（他批命 8 秒，不能跳過），
  //  加上要滑到整頁的 15% 才看得到第一塊面板（10-20 秒）——
  //  掩護 18 到 28 秒，而生成實測 6 秒。綽綽有餘。
  //
  //  startTeaser 是單發的：同一份問診資料只會真的打一次
  //  （React 嚴格模式會讓事件處理器以外的東西跑兩遍，見 feihong-teaser-cache）。
  //
  //  null 有兩種意思：還沒回來、或生成失敗。兩種的畫面一樣
  //  （那四塊整個不掛，見 renderBlock 的 panel 分支）——
  //  使用者分不出差別，而且這是付費前的鉤子不是商品，
  //  少掉四塊不該擋住整條動線。
  //  ⭐ 這裡本來有一組寫死的占位內文（SAMPLE_PANELS），給預覽模式調版型用。
  //  引擎做好之後連同它一起刪掉了（2026-10-22）—— 不留著當「生成失敗的備援」：
  //  面板講的是他自己的八字，拿一段固定文字冒充比少掉這一塊更糟，
  //  而那段占位文字還是從白夜抄來的（講的是事業與人際，跟感情無關）。
  const [panels, setPanels] = useState(null);

  //開場那顆「開始解命」按下去時由開場元件回呼。
  //  aliveRef 而不是 effect 的區域變數：這不是 effect，是事件處理器 ——
  //  使用者可能在生成回來之前就按返回離開，那時 setPanels 會對著
  //  已卸載的元件呼叫。
  //
  //  ⚠️ 進場時一定要把它設回 true，不能只靠 useRef(true) 的初始值：
  //  嚴格模式下 effect 會跑「掛載 → cleanup → 再掛載」，
  //  cleanup 把它翻成 false，而第二次掛載不會重新執行 useRef 的初始值 ——
  //  結果就是從頭到尾都是 false，setPanels 永遠不會被呼叫，
  //  四塊面板明明生成成功卻一塊都不出現。
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const beginTeaser = useCallback(() => {
    if (!intake) return;
    startTeaser(intake).then((data) => {
      if (aliveRef.current && data) setPanels(data);
    });
  }, [intake]);

  //預覽模式（?preview 或 npm start）掛載就發，不等那顆「開始解命」。
  //
  //  為什麼要這一段：正式流程是按下按鈕才發（那是意願訊號，見 beginTeaser
  //  上面的說明），但調版型時開場是跳過的 —— 不補這一下，預覽永遠看不到面板。
  //
  //  ⚠️ 它打的是**真的 API**，不是占位文字。所以預覽每重新整理一次就燒一次
  //  （startTeaser 的單發是模組層的狀態，重整就沒了）。實測一次約 NT$0.06。
  //  只調圖或文字 block 的時候，看完面板就不必一直重整。
  //
  //  conditions 跟 intake 用的是同一個 wantsSampleIntake —— 正式走完問診
  //  進來的人永遠不會踩到這段，不會變成「按鈕還沒按就先發了」。
  useEffect(() => {
    if (!wantsSampleIntake(search)) return;
    beginTeaser();
  }, [search, beginTeaser]);

  //—— 底部按鈕的出現時機 ——
  //  觸發之後就 disconnect：按鈕只出現一次，往回捲不會又消失。
  //
  //  ⚠️ 一定要等開場播完才掛這個 observer。兩個理由，第二個是會出錯的那個：
  //
  //  1. 開場期間整頁被影片蓋著也不能捲，這時候讓按鈕冒出來沒有意義。
  //
  //  2. 頁面剛掛載時那十九張圖都還沒有高度，整頁只有幾百 px ——
  //     觸發點最終在 2653px，但那一瞬間它就在視窗裡。observer 會立刻
  //     判定「滑到了」然後 disconnect，按鈕就從此固定在畫面上。
  //     這個 bug 只有在圖沒進快取時才會出現（冷啟動、第一次進站的真實使用者），
  //     開發時重整幾次之後反而看不到 —— 所以它很容易被漏掉。
  //
  //     等開場那十七秒過去，圖早就載完、版面也定了，量到的位置才是真的。
  useEffect(() => {
    if (!intake || !introDone) return undefined;

    const target = document.getElementById(CTA_TRIGGER_ID);
    const root = scrollRef.current;
    if (!target || !root) return undefined;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setShowCta(true);
        io.disconnect();
      },
      { root },
    );
    io.observe(target);

    return () => io.disconnect();
  }, [intake, introDone]);

  //沒有問診資料就不該停在這一頁（直接貼網址、或重新整理）。
  //  replace 而不是 push：這一頁不該留在返回堆疊裡，
  //  不然使用者回上一頁又會被彈回來一次。
  useEffect(() => {
    if (!intake) navigate(BACK_TO, { replace: true });
  }, [intake, navigate]);

  //返回：有上一頁就回上一頁，沒有就回劇情頁。
  //  直接貼網址進來時 history 沒有上一頁，navigate(-1) 會把人送出本站
  //  （退路判斷同 sub-header 與劇情頁）。
  const handleBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate(BACK_TO, { replace: true });
  };

  //導頁是在 effect 裡做的（render 當下還不能導），所以這一幀先不畫東西 ——
  //  畫了會閃一下沒有前提的空頁面。
  if (!intake) return null;

  return (
    <div
      className={`feihong-story${introDone ? "" : " is-intro"}`}
      ref={scrollRef}
    >
      {/* 開場：他看完竹冊、然後批命。這兩幕原本在劇情頁，
          搬過來是為了把生成那四塊命書的時間藏在劇情底下（見上方 panels 那段）。
          播完就卸載 —— 留著的話那兩支影片會一直佔著記憶體與解碼器，
          iOS 上同時掛太多 <video> 會讓後面的影片播不出來。 */}
      {!introDone && (
        <FeihongStoryIntro
          onReading={beginTeaser}
          onDone={() => setIntroDone(true)}
        />
      )}
      {/* 返回鍵浮在內容之上。不用站內的 SubHeader ——
          那是一條有底色的頂部列，會把這一頁從「戲的延續」拉回「網站的次層頁」。 */}
      <button
        type="button"
        className="feihong-story__back"
        onClick={handleBack}
        aria-label="返回"
      >
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
          <path
            d="M15 5l-7 7 7 7"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* 一個個 block 串下去。中間沒有間隙 ——
          圖和圖、圖和文之間要接得像同一張長圖，不是一段段的卡片。
          結尾的行動按鈕等內容定案再加，現在整頁就是故事本身。 */}
      <main className="feihong-story__blocks">
        {blocks.map((block, i) =>
          renderBlock(block, i, {
            pillars,
            chart,
            name: intake.name,
            birthDate: intake.birthDate,
            //對方那張盤。只有合盤的兩條線會用到，單身線沒有那一塊。
            //  刻意沒有 partnerName —— 劇情頁沒有問對方叫什麼
            //  （一個附帶的姓名欄会讓人以為非填不可），
            //  所以標題永遠是 block 的 fallbackName，就是「另一位」。
            partnerChart,
            partnerBirthDate: intake.partnerBirthDate,
            //命書面板的內文。panels 還沒回來（或失敗）時是 undefined，
            //  那幾塊就不會掛上來 —— 見上方 panels 那段說明。
            //
            //  ⭐ key 跟著 BLOCKS 的 bodyFrom 走，三條線加起來共五種：
            //    situation  三條線都有（你現在的狀態）
            //    destiny    三條線都有（感情宿命）
            //    peach      單身線專用（桃花運勢）
            //    person     單身線專用（那人的身影）
            //    bond       合盤兩條線（你們的情線）
            //  沒用到的 key 給 undefined 不會有事 —— panel 分支本來就會回 null。
            situation: panels?.situation,
            destiny: panels?.destiny,
            peach: panels?.peach,
            person: panels?.person,
            bond: panels?.bond,
          }),
        )}
      </main>

      {/* 固定在底部的行動按鈕。外層是全站共用的 .app-fixed ——
          它貼著欄位而不是視窗邊緣，桌機上才不會跑到畫面最旁邊。 */}
      {showCta && (
        <div className="app-fixed feihong-story__cta-layer">
          {/* 按鈕上的字已經畫在圖裡，所以不放文字節點。
              aria-label 仍然要給 —— 讀屏看不到圖上的字，
              沒有它這顆按鈕會被唸成「按鈕」而已。 */}
          <button
            type="button"
            className="feihong-story__cta"
            aria-label="向緋紅窺探命局"
            style={{ "--cta-bg": `url(${IMG}feihong-story-btn-2.png)` }}
            onClick={() => {
              //—— 動線打點：開啟購買視窗 ——
              track("checkout_open", "match-feihong");
              setBuying(true);
            }}
          />
        </div>
      )}

      {/* theme 不給的話會用預設那組薄荷綠（白夜的色）——
          故事頁一路紅到底，按下購買卻跳出一張綠色的單子。
          值對應 constant/products.js 緋紅那筆的 theme 欄位。 */}
      <PurchaseSheet
        open={buying}
        plans={plans}
        theme="rose"
        onClose={() => setBuying(false)}
        onSubmit={handlePickPlan}
      />

      {/* 發票資訊。在跳轉綠界之前問 ——
          電子發票是法規要求，而綠界付完款不會再問一次。
          預設信箱帶問診填的那個：這個流程沒有登入，抓不到 Google 信箱。 */}
      {pendingPlan && (
        <InvoiceSheet
          open
          amount={pendingPlan.price}
          defaultEmail={intake.email}
          defaultName={intake.name}
          submitting={submitting}
          onClose={() => {
            setPendingPlan(null);
            setPayError("");
          }}
          onConfirm={handleCheckout}
        />
      )}

      {/* 建單失敗。用浮動提示而不是換頁 ——
          使用者還在故事頁上，把他導走等於要他從頭再來一次。 */}
      {payError && (
        <div className="feihong-story__pay-error" role="alert">
          {payError}
        </div>
      )}
    </div>
  );
};

export default FeihongStoryPage;
